import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, apiBlobRequest, apiRequest } from '../apiClient';
import { buildUiHelpers } from './sharedUi';
import ContentStudioSection from './ContentStudioSection';
import AdminNewsletterContacts from './AdminNewsletterContacts';
import AccountDataExport from './AccountDataExport';
import {
  formatPlanLabel,
  formatPlanStatusLabel,
  getPlanBadgeColor,
  formatMonitoringStatusLabel,
} from './sharedFormatters';

const RSL_SIGNAL_MARKETS = [
  { key: 'SP500', label: 'S&P 500' },
  { key: 'NASDAQ100', label: 'Nasdaq 100' },
  { key: 'HDAX', label: 'HDAX' },
  { key: 'STOXX600', label: 'STOXX 600' },
  { key: 'CUSTOM', label: 'Eigene RSL-Watchlisten' },
];

const DEFAULT_RSL_SIGNAL_SETTINGS = {
  SP500: { ma_filter_enabled: true, ma_short_weeks: 5, ma_long_weeks: 13, k_exit_enabled: true, k_exit_top_pct: 25, index_compare_enabled: true, rsl_min_enabled: true, rsl_min_value: 1, daily_primary_provider: 'yahoo', daily_fallback_provider: 'marketstack' },
  NASDAQ100: { ma_filter_enabled: true, ma_short_weeks: 5, ma_long_weeks: 13, k_exit_enabled: true, k_exit_top_pct: 21, index_compare_enabled: true, rsl_min_enabled: true, rsl_min_value: 1, daily_primary_provider: 'yahoo', daily_fallback_provider: 'marketstack' },
  HDAX: { ma_filter_enabled: true, ma_short_weeks: 5, ma_long_weeks: 13, k_exit_enabled: true, k_exit_top_pct: 25, index_compare_enabled: true, rsl_min_enabled: true, rsl_min_value: 1, daily_primary_provider: 'yahoo', daily_fallback_provider: 'none' },
  STOXX600: { ma_filter_enabled: true, ma_short_weeks: 5, ma_long_weeks: 13, k_exit_enabled: true, k_exit_top_pct: 25, index_compare_enabled: true, rsl_min_enabled: true, rsl_min_value: 1, daily_primary_provider: 'yahoo', daily_fallback_provider: 'none' },
  CUSTOM: { ma_filter_enabled: true, ma_short_weeks: 5, ma_long_weeks: 13, k_exit_enabled: true, k_exit_top_pct: 25, index_compare_enabled: true, rsl_min_enabled: true, rsl_min_value: 1, daily_primary_provider: 'yahoo', daily_fallback_provider: 'none' },
};

const DATA_PROVIDER_LABELS = {
  yahoo: 'Yahoo',
  marketstack: 'Marketstack',
  none: 'Keiner',
};

const MEETING_TYPES = [
  { key: 'coaching', label: 'Coaching' },
  { key: 'support', label: 'Support' },
  { key: 'community', label: 'Community' },
  { key: 'webinar', label: 'Webinar' },
  { key: 'partner', label: 'Partnerraum' },
];

const MEET_BASE_URL = 'https://meet.einfach-trading.de';

function slugifyMeetingPart(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function normalizeRoadmapStatus(status) {
  const normalized = String(status || 'geplant')
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^\w\s-]+/g, '')
    .replace(/[-\s]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const aliases = {
    todo: 'geplant',
    planned: 'geplant',
    plan: 'geplant',
    open: 'geplant',
    in_progress: 'in_arbeit',
    inarbeit: 'in_arbeit',
    arbeit: 'in_arbeit',
    doing: 'in_arbeit',
    done: 'erledigt',
    complete: 'erledigt',
    completed: 'erledigt',
    fertig: 'erledigt',
    abgeschlossen: 'erledigt',
  };
  const statusKey = aliases[normalized]
    || (normalized.startsWith('erledig') || normalized.startsWith('fertig') || normalized.startsWith('abgeschlossen') ? 'erledigt' : null)
    || (normalized.startsWith('in_arbeit') || normalized.startsWith('inarbeit') || normalized.startsWith('inprogress') || normalized.startsWith('doing') ? 'in_arbeit' : null)
    || (normalized.startsWith('geplant') || normalized.startsWith('todo') || normalized.startsWith('planned') || normalized.startsWith('open') ? 'geplant' : null)
    || normalized;
  return ['geplant', 'in_arbeit', 'erledigt'].includes(statusKey) ? statusKey : 'geplant';
}

const ADMIN_HANDBOOK_SECTIONS = [
  {
    title: '📈 Strategien',
    purpose: 'Pflegt die Marktlisten und die operative Arbeitsgrundlage für Screener und Depot.',
    details: [
      {
        label: 'Was du hier verwaltest',
        items: [
          'Die Watchlists für HDAX und S&P 500 bilden die Datenbasis für den RSL Screener und die Depotvorschläge.',
          'Die ConversionRates-Dateien beeinflussen die Aufbereitung und Auswertung der IB-Kontoauszüge.',
          'Änderungen in diesem Bereich wirken direkt auf Datenqualität, Ranking und Vorschläge in den Fachbereichen.',
        ],
      },
      {
        label: 'Typische Aktionen',
        items: [
          'Watchlists neu laden, bearbeiten und speichern, wenn sich Titel oder Marktlisten ändern.',
          'ConversionRates-Dateien austauschen, wenn sich die Datenquelle oder die verwendeten Jahre ändern.',
          'Nach jeder Änderung kurz im Screener oder Depot prüfen, ob die betroffenen Märkte wieder plausible Daten liefern.',
        ],
      },
      {
        label: 'Worauf du achten solltest',
        items: [
          'Fehler in den Listen führen nicht zu kleinen Abweichungen, sondern oft zu komplett fehlenden oder falschen Top-Signalen.',
          'Wenn bei einzelnen Märkten plötzlich viele Werte fehlen oder rot wirken, zuerst hier prüfen und danach Monitoring oder Cache-Refresh anschauen.',
        ],
      },
      {
        label: 'RSL-Berechnung und Vergleich mit Google',
        items: [
          'Unterschied zur Google-Formel: Google nutzt aktueller Live-Preis geteilt durch Mittelwert aus 26 Weekly-Werten plus Live-Preis. Der Code nutzt letzter Weekly-Close geteilt durch Mittelwert aus 27 Weekly-Closes inklusive dieses letzten Close.',
          'Die Logik ist damit nahezu identisch: Im Nenner stehen 27 Werte und der neueste verfügbare Wert steckt auch im Zähler.',
          'Der wichtigste Unterschied liegt in der Datenquelle. Google Sheets arbeitet mit einem aktuellen price, unser Code mit Yahoo Finance.',
          'Yahoo Finance liefert in dieser Weekly-Berechnung keinen garantierten Echtzeitkurs, sondern den letzten verfügbaren Wert aus der laufenden Wochenserie.',
          'yFinance mit interval gleich 1wk liefert Wochenkerzen. Die letzte Kerze ist die aktuelle, noch laufende Woche und verändert sich im Verlauf der Woche weiter.',
          'Konkret: Am Dienstag enthält die letzte Weekly-Kerze typischerweise den Stand der laufenden Woche, häufig nahe am letzten verfügbaren Tagesschlusskurs. Am Freitag bildet sie fast die ganze Handelswoche ab.',
          'Yahoo aktualisiert den letzten Wochenwert verzögert. Es ist also weder ein sauberer Echtzeitkurs noch einfach nur gestern im klassischen Sinn.',
          'Fazit: Die RSL basiert auf dem letzten verfügbaren Kursstand innerhalb der laufenden Wochenkerze, mit möglicher Verzögerung gegenüber dem Markt.',
          'Der sichtbare Kurs im Screener kann trotzdem aktueller sein als die RSL-Basis, weil der Kurs separat aus Tagesdaten geladen wird.',
          'Wenn Abweichungen zu Google gemeldet werden, immer drei Dinge getrennt prüfen: sichtbarer Kursstand, Marktdaten-Stand im Monitoring und die eigentliche RSL-Rangfolge.',
        ],
      },
    ],
  },
  {
    title: '👥 User Verwaltung',
    purpose: 'Verwaltet Accounts, Zugriffsstatus, Rollen, Module und Depotdaten einzelner User.',
    details: [
      {
        label: 'Was du hier siehst',
        items: [
          'Die Tabelle zeigt E-Mail, Rolle, Mitgliedschaft, Account-Zustand, letzten Login und die verfügbaren Aktionen.',
          'Mitgliedschaft bündelt den effektiven Plan, den Plan-Status und den Billing-Status in einer Spalte.',
          'Account zeigt, ob der Benutzer aktiv ist und ob Trial-, Abo- oder Zugangsfristen gesetzt sind.',
          'Die Tabelle ist absichtlich kompakt. Detailarbeit läuft danach über Bearbeiten, Billing-Historie und Sync.',
        ],
      },
      {
        label: 'Filter und Suche',
        items: [
          'Die Suche filtert gezielt nach E-Mail-Adressen und ist für Support der schnellste Einstieg.',
          'Die Filter helfen dir, Trial-User, zahlende User, offene Billing-Fälle oder User ohne Billing schneller zu finden.',
          'Zahlend bedeutet in der Praxis: Billing-Status aktiv oder trialing. Billing offen zeigt unvollständige oder problematische Zahlungszustände.',
        ],
      },
      {
        label: 'Bedeutung von Plan und Status',
        items: [
          'Plan beschreibt die fachliche Mitgliedschaft in der App: Free, Essential oder Plus.',
          'Plan-Status beschreibt den Zustand dieses Plans: Trial, Aktiv, Gekündigt oder Abgelaufen.',
          'Der effektive Zugriff kann aus manuellem Plan und Billing-Plan zusammengesetzt sein. Deshalb ist der sichtbare Endzustand wichtiger als einzelne Rohfelder.',
          'Wenn ein User nach Ablauf von Trial oder Abo nur Start und Konto sieht, ist das kein Fehler, sondern der normale Rückfall auf den eingeschränkten Zustand.',
        ],
      },
      {
        label: 'Wichtige Datumsfelder',
        items: [
          'Trial bis steuert das Ende des 30-Tage-Testzugangs.',
          'Abo bis ist die fachliche Laufzeit für manuell gesetzte Essential- oder Plus-Zugänge.',
          'Zugang bis ist ein übergeordnetes Sonderfeld für zeitlich begrenzten Gesamtzugang oder eine manuelle Gesamtsperre.',
          'Wenn mehrere Datumsfelder gesetzt sind, immer prüfen, welches Feld den realen Zugriff begrenzt.',
        ],
      },
      {
        label: 'Bearbeitungsformular',
        items: [
          'Hier steuerst du Plan, Plan-Status, Trial bis, Abo bis, Zugang bis und Admin-Rechte.',
          'Manuelle Modulfreigaben sind für Sonderfälle gedacht, wenn ein User Zugriff braucht, ohne dass sein Plan geändert werden soll.',
          'Depot- und Moduldaten im Edit-Modus helfen, Supportfälle direkt am User zu lösen.',
          'Admin-Rechte nur setzen, wenn der Nutzer wirklich Zugriff auf interne Verwaltungsfunktionen erhalten soll.',
          'Ein leer gelassenes Passwort im Bearbeitungsformular bedeutet: Passwort bleibt unverändert.',
        ],
      },
      {
        label: 'Aktionen rechts',
        items: [
          '💳 öffnet den Zahlungsverlauf des Users direkt im Admin.',
          '↻ synchronisiert die Stripe-Historie nur für diesen User nach.',
          '✏️ öffnet die Bearbeitung des Users.',
          '🗑️ löscht den User administrativ und greift in den bestehenden Lösch-/Reservierungsflow ein.',
          'Neu laden im offenen Billing-Verlauf lädt nur die Historie neu und schließt die Ansicht nicht.',
        ],
      },
      {
        label: 'Besondere Kontozustände',
        items: [
          'Wenn ein User seinen Account selbst löscht, wird der Zugang zuerst deaktiviert und kann 30 Tage lang durch erneute Registrierung mit derselben E-Mail reaktiviert werden.',
          'In dieser 30-Tage-Phase bleiben Depot- und Historieneinträge erhalten. Erst danach greift die endgültige Löschlogik.',
          'Drei Tage vor der endgültigen Löschung versendet die App automatisch eine Erinnerungs-Mail an den Nutzer.',
          'Wenn diese 3-Tage-Erinnerung verschickt wird, erhalten aktive Admins parallel eine Info-Mail darüber.',
          'Wenn zum Zeitpunkt der endgültigen Löschung noch ein aktives Stripe-Abo existiert, versucht die App dieses Abo zuerst zu kündigen.',
          'Scheitert diese Stripe-Kündigung, wird die endgültige Löschung aktuell bewusst nicht abgeschlossen und aktive Admins werden zusätzlich per Mail informiert.',
          'Deaktivierte Accounts melden beim Login, dass eine neue Registrierung nötig ist, um den Zugang wieder zu reaktivieren.',
        ],
      },
      {
        label: '2FA, Verifizierung und Risiko-Hinweis',
        items: [
          '2FA wird erst aktiv, wenn der Nutzer den per E-Mail erhaltenen Code bestätigt hat.',
          'Wenn ein User bei 2FA-Problemen hängt, immer prüfen, ob 2FA schon aktiv oder noch pending ist.',
          'Auch Verifizierungs- und Risiko-Hinweis-Zustände sind nutzerrelevant, wenn ein Login scheinbar klappt, aber danach kein Zugriff erfolgt.',
        ],
      },
      {
        label: 'Typische Supportfälle',
        items: [
          'Bei Login- oder Zugriffsproblemen zuerst Rolle, Plan, Plan-Status, Account-Zustand und letzte Anmeldung prüfen.',
          'Bei Trial- oder Billing-Fragen zusätzlich Trial bis, Abo bis und Billing-Status vergleichen.',
          'Wenn ein Account vom Nutzer selbst deaktiviert wurde, kann die Reaktivierung über erneute Registrierung mit derselben E-Mail erfolgen.',
          'Wenn kurz vor einer endgültigen Löschung Fragen auftauchen, prüfen, ob die 3-Tage-Erinnerung bereits versendet wurde und ob Admins dazu eine Info-Mail erhalten haben.',
          'Wenn ein deaktivierter Account trotz Fristablauf nicht final gelöscht wurde, auch prüfen, ob eine Stripe-Kündigung im Final-Löschschritt fehlgeschlagen ist und ob dazu eine Admin-Mail eingegangen ist.',
          'Wenn ein User “zu früh ausgeloggt” wird, ist oft nicht der Plan schuld, sondern ein Session-, Cookie- oder 2FA-Thema.',
          'Wenn Funktionen fehlen, zuerst prüfen, ob es wirklich ein Planproblem ist oder nur ein Modulproblem.',
        ],
      },
      {
        label: 'Arbeitsregel für Support',
        items: [
          'Immer zuerst den effektiven Zustand lesen, nicht nur einzelne Rohfelder.',
          'Bei Konflikten zwischen Billing und manuellem Plan gilt: prüfen, welche Berechtigung für den Nutzer tatsächlich gezogen wird.',
          'Vor manuellen Korrekturen möglichst verstehen, ob das Problem aus Auth, Billing, Trial oder Modulsteuerung kommt.',
        ],
      },
    ],
  },
  {
    title: '📣 Content Studio',
    purpose: 'Erzeugt aus Screener-, Depot- und Produktdaten direkt nutzbare Vorlagen für Instagram und TikTok.',
    details: [
      {
        label: 'Was du hier bekommst',
        items: [
          'Der Bereich zieht aktuelle Daten aus Community Depot, Screener und App-Changelog zusammen.',
          'Daraus entstehen automatisch Hook, Caption, Carousel-Struktur und Reel-Skript für typische Social-Formate.',
          'Der Bereich ist bewusst nur für Admins sichtbar, weil hier interne Arbeitsvorlagen und Produktkommunikation vorbereitet werden.',
        ],
      },
      {
        label: 'Wie du damit arbeitest',
        items: [
          'Wähle links eine aktuelle Idee aus und prüfe zuerst, ob sie zur Tageslage und zum Markt passt.',
          'Kopiere danach Hook, Caption oder Skript direkt in dein Redaktions- oder Video-Tool.',
          'Nutze Community-Depot-Updates für Transparenz, Top-Signale für tägliche Relevanz und Erklärungen für Vertrauen und Education.',
        ],
      },
      {
        label: 'Worauf du achten solltest',
        items: [
          'Nicht als Kaufaufforderung formulieren, sondern als transparenter Blick auf relative Stärke, Signale und Regelwerk.',
          'Educational Content funktioniert meist besser, wenn er an echten Depot- oder Screener-Beispielen erklärt wird.',
          'Produkt-Updates nur posten, wenn der Nutzen für Nutzer klar verständlich ist und nicht nur interne Technik kommuniziert wird.',
        ],
      },
    ],
  },
  {
    title: '⚙ App Einstellungen',
    purpose: 'Enthält technische Konfigurationen für Mailversand und allgemeine App-Funktionen.',
    details: [
      {
        label: 'SMTP / Mailversand',
        items: [
          'SMTP Host, Port, User, Absenderadresse und Passwort steuern den gesamten transaktionalen Mailverkehr der App.',
          'Davon hängen Registrierung, Verifizierung, Passwort-Reset, 2FA-Codes, Support-Antworten und Newsletter ab.',
          'Wenn Mails nicht ankommen, zuerst diese Konfiguration und danach den betroffenen User-Flow prüfen.',
        ],
      },
      {
        label: 'Signals / Premium-Mailversand',
        items: [
          'Der Schalter für Signal-Mails entscheidet, ob Premium-Signale per Mail versendet werden dürfen.',
          'Das ist relevant für Supportfälle, bei denen User Signale in der App sehen, aber keine E-Mail erhalten.',
        ],
      },
      {
        label: 'UI-Einstellungen',
        items: [
          'Globale UI-Schalter wie TradingView-Charts aktivieren oder deaktivieren Funktionen für alle Nutzer gleichzeitig.',
          'Diese Schalter sind hilfreich, wenn eine eingebettete Drittansicht Probleme macht oder bewusst abgeschaltet werden soll.',
        ],
      },
    ],
  },
  {
    title: '💳 Billing',
    purpose: 'Verwaltet die Stripe-Anbindung, verfügbare Produkte und den Import historischer Zahlungen.',
    details: [
      {
        label: 'Grundkonfiguration',
        items: [
          'Hier pflegst du Billing an/aus, Provider, Publishable Key, Secret Key, Webhook Secret und Customer Portal.',
          'Success- und Cancel-URL steuern, wohin User nach Checkout oder Abbruch zurückkehren.',
          'Wenn Checkout oder Portal nicht funktionieren, zuerst diese Felder prüfen.',
        ],
      },
      {
        label: 'Live vs. Testbetrieb',
        items: [
          'Stripe trennt Test und Live vollständig. Test-Price-IDs und Test-Keys funktionieren live nicht.',
          'Für den Wechsel in den Echtbetrieb müssen Live-Keys, Live-Webhook und Live-Price-IDs separat in Billing hinterlegt werden.',
          'Wenn ein Checkout zwar öffnet, aber in der falschen Umgebung landet, ist fast immer Test/Live vermischt.',
        ],
      },
      {
        label: 'Produkte und Preis-IDs',
        items: [
          'Für Essential monatlich, Essential jährlich, Plus monatlich, Plus jährlich und Anleitung gibt es jeweils eigene Price IDs.',
          'Jedes Produkt kann separat aktiviert oder deaktiviert werden, ohne die Price ID zu löschen.',
          'Deaktivierte Produkte erscheinen nicht mehr im Konto und werden serverseitig auch nicht mehr zum Checkout zugelassen.',
          'Wenn ein Produkt im Konto fehlt, zuerst den Produktschalter prüfen und danach die hinterlegte Price ID.',
        ],
      },
      {
        label: 'Wie Billing den Zugriff steuert',
        items: [
          'Stripe schreibt nicht blind in den manuellen Plan des Users, sondern in den getrennten Billing-Zustand.',
          'Der effektive Zugriff ergibt sich aus manuellem Plan, Plan-Status und Billing-Plan gemeinsam.',
          'Dadurch bleiben manuelle Gratisfreigaben oder Sonderzugänge möglich, auch wenn Billing zusätzlich existiert.',
        ],
      },
      {
        label: 'Billing-Status verstehen',
        items: [
          'active bedeutet: das Abo ist wirksam und der Zugriff soll freigeschaltet sein.',
          'trialing bedeutet: Stripe verwaltet eine Testphase im Abo-Modell.',
          'incomplete oder unpaid bedeuten: Checkout oder Zahlung ist noch nicht sauber abgeschlossen.',
          'past_due bedeutet: ein Zahlungsproblem ist offen, der Zugriff kann je nach Logik noch vorübergehend bestehen.',
          'canceled bedeutet: gekündigt, oft noch bis zum Periodenende wirksam.',
        ],
      },
      {
        label: 'Historie und Synchronisation',
        items: [
          'Der globale Import zieht bestehende Stripe-Rechnungen in den lokalen Zahlungsverlauf.',
          'In der Userverwaltung kannst du die Historie einzelner User öffnen oder gezielt nachsynchronisieren.',
          'Das ist besonders hilfreich nach neuen Webhook-Logiken oder wenn alte Zahlungen nachträglich sichtbar sein sollen.',
          'Der lokale Zahlungsverlauf ist wichtig für Support, weil er ohne direkten Wechsel ins Stripe-Dashboard lesbar ist.',
        ],
      },
      {
        label: 'Webhook und Betriebslogik',
        items: [
          'Der Webhook ist die zentrale Quelle für Statuswechsel wie Checkout abgeschlossen, Rechnung bezahlt oder Zahlung fehlgeschlagen.',
          'Wenn Stripe selbst korrekt aussieht, aber die App nicht, zuerst prüfen, ob Webhook-Ereignisse verarbeitet wurden.',
          'Fehlende oder falsche Status in der App deuten oft auf Webhook- oder Synchronisationsprobleme hin, nicht auf ein Frontend-Problem.',
        ],
      },
      {
        label: 'Typische Supportfälle',
        items: [
          'Wenn ein User bezahlt hat, aber keinen Zugriff sieht: zuerst Billing-Status, Billing-Plan und Zahlungsverlauf prüfen.',
          'Wenn Portal oder Checkout leer laufen: Keys, Webhook Secret, Price IDs und Produktschalter kontrollieren.',
          'Wenn historische Rechnungen fehlen: Import oder User-Sync verwenden.',
          'Wenn Anleitung keinen Beleg zeigt, prüfen, ob der Receipt-Link für den Einmalzahlungs-Checkout korrekt übernommen wurde.',
          'Wenn ein User mehrfach auf Checkout war, aber kein wirksames Abo hat, besonders auf incomplete, unpaid und canceled achten.',
        ],
      },
      {
        label: 'Empfohlener Prüfablauf',
        items: [
          '1. Produkt sichtbar und aktiviert?',
          '2. Price ID korrekt?',
          '3. Checkout erreichbar?',
          '4. Webhook verarbeitet?',
          '5. Billing-Status und Billing-Plan am User korrekt?',
          '6. Zahlungsverlauf plausibel?',
        ],
      },
    ],
  },
  {
    title: '🩺 App System',
    purpose: 'Zeigt Betriebszustand, Monitoring, Security-Events und technische Prüfpunkte der App.',
    details: [
      {
        label: 'Monitoring',
        items: [
          'Monitoring fasst technische Checks wie Datenbankstand, Migrationszustand und allgemeine Systemprüfungen zusammen.',
          'Bei Betriebsproblemen ist das der erste Bereich, um schnell zwischen Fachproblem und Systemproblem zu unterscheiden.',
        ],
      },
      {
        label: 'Security-Events',
        items: [
          'Security-Events dokumentieren sicherheitsrelevante Vorgänge wie fehlgeschlagene Authentifizierung, Schutzmaßnahmen oder ungewöhnliche Zustände.',
          'Dieser Bereich ist wichtig, wenn User von wiederholten Logout-, Login- oder Schutzmeldungen berichten.',
        ],
      },
      {
        label: 'Logs und Diagnose',
        items: [
          'Wenn Fehler nicht fachlich erklärbar sind, sollte dieser Bereich zusammen mit den Server-Logs betrachtet werden.',
          'Monitoring und Security helfen dabei, die richtige Eskalationsrichtung zu wählen: Daten, Auth, Billing oder Infrastruktur.',
        ],
      },
    ],
  },
  {
    title: '🧩 Produkt-Pipeline',
    purpose: 'Pflegt Features, Roadmap und Changelog für die Weiterentwicklung der Plattform.',
    details: [
      {
        label: 'Feature-Liste',
        items: [
          'Hier sammelst du neue Ideen, Anforderungen und Aufgaben, bevor sie verbindlich geplant werden.',
          'Ein Feature kann direkt aus der Liste in die Roadmap übernommen werden.',
        ],
      },
      {
        label: 'Roadmap',
        items: [
          'In der Roadmap steuerst du Status, Zieltermin, Beschreibung und ob der Punkt im Changelog erscheinen soll.',
          'Die Filter helfen, geplante, laufende und erledigte Punkte getrennt zu betrachten.',
        ],
      },
      {
        label: 'Changelog',
        items: [
          'Das Changelog wird aus ausgewählten Roadmap-Punkten abgeleitet und ist die externe Nutzerkommunikation.',
          'Nicht jede interne Aufgabe gehört ins Changelog. Nur sichtbare oder relevante Produktänderungen sollten dort auftauchen.',
        ],
      },
      {
        label: 'Empfohlener Ablauf',
        items: [
          'Neue Idee als Feature anlegen.',
          'Bei Relevanz in die Roadmap übernehmen und dort terminieren.',
          'Nach Fertigstellung für das Changelog markieren, damit Nutzer die Änderung sehen.',
        ],
      },
    ],
  },
  {
    title: '📣 Newsletter',
    purpose: 'Ermöglicht den Versand von Mails an ausgewählte Benutzer direkt aus dem Admin.',
    details: [
      {
        label: 'Wofür der Bereich gedacht ist',
        items: [
          'Newsletter ist für gezielte Kommunikation mit ausgewählten Empfängern gedacht, nicht für technische Pflichtmails.',
          'Der Versand nutzt die SMTP-Konfiguration aus den App-Einstellungen.',
        ],
      },
      {
        label: 'Worauf du achten solltest',
        items: [
          'Vor größeren Aussendungen Betreff, Inhalt und Empfängerauswahl sorgfältig prüfen.',
          'Wenn der Versand fehlschlägt, zuerst SMTP prüfen und dann den eigentlichen Versandversuch nachvollziehen.',
        ],
      },
    ],
  },
  {
    title: '🧭 Support-Logik im Alltag',
    purpose: 'Hilft bei typischen Supportfällen und der richtigen Stelle im Admin.',
    details: [
      {
        label: 'Login und Zugriff',
        items: [
          'Zuerst in die Userverwaltung: Rolle, Mitgliedschaft, Account-Zustand, letzte Anmeldung und Laufzeiten prüfen.',
          'Nach erfolgreichem Login landet der Nutzer standardmäßig im Depot und nicht mehr im Screener.',
          'Bei Trial-Ende oder abgelaufenem Abo sollte der Zugriff auf Start und Konto reduziert sein.',
          'Bei deaktivierten Accounts den Reaktivierungsweg über erneute Registrierung mit derselben E-Mail beachten.',
          'Bei deaktivierten Accounts kurz vor der Frist zusätzlich beachten, dass drei Tage vor der endgültigen Löschung automatisch eine Erinnerungs-Mail ausgelöst wird.',
        ],
      },
      {
        label: 'Billing und Zahlungen',
        items: [
          'Zuerst Billing-Konfiguration prüfen, danach den Zahlungsverlauf des Users öffnen.',
          'Bei fehlender Historie oder unstimmigem Status User-Sync oder globalen Stripe-Import verwenden.',
          'Wenn ein selbst deaktivierter Account nach 30 Tagen final gelöscht wird, versucht die App ein vorhandenes Stripe-Abo vorher aktiv zu kündigen.',
          'Wenn diese Stripe-Kündigung beim Final-Delete fehlschlägt, bleibt der Account im Pending-Delete-Zustand stehen und aktive Admins bekommen dazu eine Info-Mail.',
        ],
      },
      {
        label: 'Mail und Auth',
        items: [
          'Bei Problemen mit Verifizierung, Passwort-Reset oder 2FA zuerst SMTP prüfen.',
          'Danach den konkreten Auth-Flow nachstellen: Registrierung, Code-Versand, Login, 2FA-Bestätigung.',
        ],
      },
      {
        label: 'Datenqualität',
        items: [
          'Wenn Screener oder Depot unplausibel wirken, zuerst die Marktlisten und danach Monitoring bzw. Caches prüfen.',
          'So lässt sich schnell unterscheiden, ob ein Datenproblem oder ein UI-/Rechenproblem vorliegt.',
        ],
      },
      {
        label: 'Support-Tickets im Alltag',
        items: [
          'Offene Tickets werden standardmäßig angezeigt. Geschlossene Tickets bleiben über den Umschalter Geschlossen weiterhin einsehbar.',
          'Wenn ein geschlossenes Ticket in der Standardansicht fehlt, ist das kein Datenverlust, sondern der gewollte Filter.',
          'Das Löschen eines Tickets ist bewusst geschützt: im Ticket muss zur Bestätigung genau löschen eingegeben werden.',
        ],
      },
    ],
  },
  {
    title: '🗺 User-Flow von Registrierung bis Reaktivierung',
    purpose: 'Beschreibt die komplette Nutzerreise mit Trial, Billing, Abo-Ende, Account-Deaktivierung und Wiederherstellung.',
    details: [
      {
        label: '1. Registrierung und Verifizierung',
        items: [
          'Ein neuer Nutzer registriert sich mit E-Mail und Passwort und muss danach seine E-Mail-Adresse per Code bestätigen.',
          'Ohne bestätigte E-Mail ist der Account noch nicht vollständig freigeschaltet.',
          'Nach erfolgreicher Verifizierung landet der Nutzer wieder im Login und kann sich regulär anmelden.',
        ],
      },
      {
        label: '2. Startzustand nach der Registrierung',
        items: [
          'Neue Nutzer starten fachlich im 30-Tage-Trial.',
          'Während dieser Zeit stehen die vorgesehenen Testfunktionen zur Verfügung, ohne dass sofort ein Abo nötig ist.',
          'Im Admin lässt sich dieser Zustand über Plan, Plan-Status und Trial-Datum nachvollziehen.',
        ],
      },
      {
        label: '3. Trial aktiv',
        items: [
          'Solange der Trial läuft, hat der Nutzer Zugriff auf die für den Test vorgesehenen Funktionen.',
          'Supportfälle in dieser Phase betreffen häufig Verifizierung, Risiko-Hinweis, 2FA oder Mailzustellung, nicht Billing.',
          'Nach Login oder erneuter Session-Herstellung landet der Nutzer im Regelfall direkt im Depot.',
          'Wenn ein Nutzer schon während des Trials ein Abo abschließt, übernimmt Billing den wirksamen Zugang.',
        ],
      },
      {
        label: '4. Trial abgelaufen ohne Zahlung',
        items: [
          'Nach Ablauf der 30 Tage fällt der Nutzer ohne wirksames Billing auf den eingeschränkten Zustand zurück.',
          'Dann sind nur noch Start und Konto verfügbar.',
          'Die App zeigt einen Hinweis, dass ein Upgrade auf Essential oder Plus nötig ist, um wieder Zugriff zu erhalten.',
        ],
      },
      {
        label: '5. Abschluss eines Abos',
        items: [
          'Im Konto kann der Nutzer Essential monatlich, Essential jährlich, Plus monatlich, Plus jährlich oder Anleitung buchen, sofern das jeweilige Produkt aktiviert ist.',
          'Der Checkout läuft über Stripe. Die App leitet danach über Success- oder Cancel-URL zurück ins Konto.',
          'Der eigentliche Billing-Status wird nicht nur durch die Rückleitung, sondern vor allem durch die Webhook-Verarbeitung sauber gesetzt.',
        ],
      },
      {
        label: '6. Billing aktiv',
        items: [
          'Wenn Stripe und Webhook sauber verarbeitet wurden, setzt die App Billing-Plan, Billing-Status und den Zahlungsverlauf.',
          'Der effektive Zugriff ergibt sich dann aus Billing plus eventuell gesetztem manuellem Plan.',
          'In Supportfällen zuerst prüfen: Produkt aktiv, Price ID korrekt, Webhook angekommen, Billing-Status plausibel.',
        ],
      },
      {
        label: '7. Kündigung oder auslaufendes Abo',
        items: [
          'Ein gekündigtes Abo kann je nach Stripe-Zustand bis zum Periodenende weiter aktiv sein.',
          'Nach dem Ende eines wirksamen Abos fällt der Nutzer wieder auf den eingeschränkten Zustand zurück, also Start und Konto.',
          'Das ist fachlich derselbe reduzierte Zugriff wie nach einem abgelaufenen Trial ohne Zahlung.',
        ],
      },
      {
        label: '8. Account selbst löschen / deaktivieren',
        items: [
          'Wenn ein Nutzer seinen Account selbst löscht, wird der Zugang nicht sofort endgültig vernichtet, sondern zunächst deaktiviert.',
          'Ab diesem Zeitpunkt läuft ein 30-Tage-Zeitraum, in dem eine Wiederherstellung möglich ist.',
          'Depot, Historie und Kontokontext bleiben in dieser Phase erhalten.',
          'Drei Tage vor dem endgültigen Ablauf verschickt die App automatisch eine Erinnerungs-Mail an den Nutzer.',
        ],
      },
      {
        label: '9. Reaktivierung innerhalb von 30 Tagen',
        items: [
          'Zur Reaktivierung registriert sich der Nutzer erneut mit derselben E-Mail-Adresse.',
          'Die App erkennt den deaktivierten Account und stellt ihn innerhalb der 30 Tage wieder her.',
          'Es wird kein neuer Gratis-Start erzeugt. Trial- und Billing-Zustände werden nicht künstlich neu begonnen, sondern der bestehende Kontokontext wird wieder aufgenommen.',
        ],
      },
      {
        label: '10. Nach Ablauf der 30 Tage',
        items: [
          'Wenn keine Reaktivierung erfolgt, greift danach die endgültige Löschlogik.',
          'Falls noch ein aktives Stripe-Abo hinterlegt ist, versucht die App dieses Abo vor der endgültigen Löschung zuerst aktiv zu kündigen.',
          'Wenn diese Stripe-Kündigung fehlschlägt, wird die endgültige Löschung aktuell nicht durchgeführt, damit kein Nutzer ohne lokale Daten, aber mit weiterlaufender Abbuchung endet.',
          'In diesem Fehlerfall erhalten aktive Admins zusätzlich eine Info-Mail, damit der Fall operativ nachverfolgt werden kann.',
          'Die Daten werden vollständig entfernt und die E-Mail landet in den reservierten E-Mails.',
          'Ab diesem Punkt ist keine normale Wiederherstellung des alten Kontos mehr vorgesehen.',
        ],
      },
      {
        label: '11. Admin-Info bei der 3-Tage-Erinnerung',
        items: [
          'Wenn die 3-Tage-Erinnerung an den Nutzer erfolgreich versendet wurde, bekommen aktive Admins zusätzlich eine Info-Mail.',
          'Damit können Support und Betrieb nachvollziehen, dass kurz vor der endgültigen Löschung bereits automatisch informiert wurde.',
        ],
      },
      {
        label: '12. Admin-Info bei fehlgeschlagener Stripe-Kündigung',
        items: [
          'Wenn die Stripe-Kündigung vor der endgültigen Löschung fehlschlägt, bekommen aktive Admins zusätzlich eine Info-Mail.',
          'Damit kann der Fall manuell nachverfolgt werden, bevor ein deaktivierter Account mit laufender Abbuchung hängen bleibt.',
        ],
      },
      {
        label: '13. Typische Sonderfälle',
        items: [
          'Nutzer sieht nach Zahlung keinen Zugriff: Billing-Status oder Webhook prüfen, nicht nur die Checkout-Rückleitung.',
          'Nutzer meldet “Account deaktiviert”: über erneute Registrierung mit derselben E-Mail auf den 30-Tage-Reaktivierungsweg führen.',
          'Nutzer denkt, ein neuer Trial startet nach Reaktivierung: das ist nicht vorgesehen, der alte Kontozustand bleibt maßgeblich.',
          'Nutzer war deaktiviert und hätte endgültig gelöscht werden sollen, existiert aber noch: prüfen, ob die Stripe-Kündigung im Final-Delete fehlgeschlagen ist.',
          'Bei 2FA-Aktivierung beachten: 2FA wird erst nach Bestätigung des per E-Mail gesendeten Codes wirklich aktiv.',
        ],
      },
    ],
  },
];

export default function AdminPanel({
  onSessionExpired,
  onIbTaxYearsChanged,
  themeMode,
  onToast,
  onConfirm,
  COLORS,
  th,
  td,
  inputStyle,
  PLAN_MODULE_MATRIX_ROWS,
  MODULE_LIST,
  PLAN_LIST,
  PLAN_STATUS_LIST,
  SUPPORT_CATEGORIES,
  SUPPORT_STATUSES,
  SIGNAL_KINDS,
}) {
  const { Card, Btn, Badge, SortHeader } = useMemo(() => buildUiHelpers({ COLORS, th }), [COLORS, th]);
  const moduleLabelMap = useMemo(
    () => Object.fromEntries((PLAN_MODULE_MATRIX_ROWS || []).map(entry => [entry.module, entry.label])),
    [PLAN_MODULE_MATRIX_ROWS]
  );
  const [adminSection, setAdminSection] = useState('users');
  const [indexCheckReminder, setIndexCheckReminder] = useState({
    should_show: false,
    message: 'Indizes auf Änderungen prüfen',
    period_key: '',
    acknowledged: false,
  });
  const [indexCheckReminderSaving, setIndexCheckReminderSaving] = useState(false);
  const [indexCheckReminderErr, setIndexCheckReminderErr] = useState('');
  const usersMetaLoadedRef = useRef(false);
  const settingsLoadedRef = useRef(false);
  const billingLoadedRef = useRef(false);
  const systemLoadedRef = useRef(false);
  const strategiesLoadedRef = useRef(false);
  const dataProvidersLoadedRef = useRef(false);
  const productLoadedRef = useRef(false);
  const liveAgentsLoadedRef = useRef(false);
  const liveAgentSetupLoadedRef = useRef(false);
  const [users, setUsers]       = useState([]);
  const [liveAgents, setLiveAgents] = useState([]);
  const [liveAgentsErr, setLiveAgentsErr] = useState('');
  const [liveAgentsUpdatedAt, setLiveAgentsUpdatedAt] = useState('');
  const [selectedLiveAgentKey, setSelectedLiveAgentKey] = useState('');
  const [liveAgentCopyMsg, setLiveAgentCopyMsg] = useState('');
  const [userStats, setUserStats] = useState({
    total_users: 0,
    active_users: 0,
    verified_users: 0,
    admins: 0,
    free_users: 0,
    basic_users: 0,
    premium_users: 0,
    trial_users: 0,
    recent_logins_24h: 0,
    recent_logins_7d: 0,
  });
  const [userStatsErr, setUserStatsErr] = useState('');
  const [reservedEmails, setReservedEmails] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [changelogEntries, setChangelogEntries] = useState([{ entry_date: '', change: '' }]);
  const [changelogMsg, setChangelogMsg] = useState('');
  const [changelogErr, setChangelogErr] = useState('');
  const [featureEntries, setFeatureEntries] = useState([{ id: '', title: '', description: '', priority: 'mittel' }]);
  const [featureRevision, setFeatureRevision] = useState(0);
  const [featureMsg, setFeatureMsg] = useState('');
  const [featureErr, setFeatureErr] = useState('');
  const [roadmapEntries, setRoadmapEntries] = useState([{ title: '', description: '', status: 'geplant', target: '', show_in_changelog: false, changelog_date: '' }]);
  const [roadmapFilters, setRoadmapFilters] = useState({ geplant: true, in_arbeit: true, erledigt: false });
  const [roadmapRevision, setRoadmapRevision] = useState(0);
  const [roadmapMsg, setRoadmapMsg] = useState('');
  const [roadmapErr, setRoadmapErr] = useState('');
  const [planModuleMatrixEntries, setPlanModuleMatrixEntries] = useState(
    PLAN_MODULE_MATRIX_ROWS.map(entry => ({ ...entry, trial: false, basic: false, premium: false }))
  );
  const [planModuleMatrixRevision, setPlanModuleMatrixRevision] = useState(0);
  const [planModuleMatrixMsg, setPlanModuleMatrixMsg] = useState('');
  const [planModuleMatrixErr, setPlanModuleMatrixErr] = useState('');
  const [changelogRevision, setChangelogRevision] = useState(0);
  const [smtp, setSmtp] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_from: '',
    smtp_tls: true,
    smtp_pass: '',
    smtp_pass_set: false,
    signals_notify_premium: true,
  });
  const [billing, setBilling] = useState({
    billing_enabled: false,
    billing_provider: 'stripe',
    billing_public_key: '',
    billing_secret_key: '',
    billing_secret_key_set: false,
    billing_webhook_secret: '',
    billing_webhook_secret_set: false,
    billing_portal_enabled: true,
    billing_success_url: '',
    billing_cancel_url: '',
    basic_monthly_enabled: true,
    basic_yearly_enabled: true,
    premium_monthly_enabled: true,
    premium_yearly_enabled: true,
    coaching_enabled: true,
    stripe_price_basic_monthly: '',
    stripe_price_basic_yearly: '',
    stripe_price_premium_monthly: '',
    stripe_price_premium_yearly: '',
    stripe_price_coaching: '',
  });
  const [billingMsg, setBillingMsg] = useState('');
  const [billingErr, setBillingErr] = useState('');
  const [billingInvoiceExportMsg, setBillingInvoiceExportMsg] = useState('');
  const [billingInvoiceExportErr, setBillingInvoiceExportErr] = useState('');
  const [billingImporting, setBillingImporting] = useState(false);
  const currentBillingExportDate = new Date();
  const [billingInvoiceExport, setBillingInvoiceExport] = useState({
    year: currentBillingExportDate.getFullYear(),
    month: currentBillingExportDate.getMonth() + 1,
    status: 'paid',
    documentType: 'invoice',
  });
  const [billingInvoiceExportLoading, setBillingInvoiceExportLoading] = useState(false);
  const [selectedBillingUser, setSelectedBillingUser] = useState(null);
  const [selectedBillingHistory, setSelectedBillingHistory] = useState([]);
  const [selectedBillingHistoryLoading, setSelectedBillingHistoryLoading] = useState(false);
  const [selectedBillingHistoryErr, setSelectedBillingHistoryErr] = useState('');
  const [uiSettings, setUiSettings] = useState({
    tradingview_charts_enabled: false,
    marketstack_api_key: '',
    marketstack_api_key_set: false,
    clear_marketstack_api_key: false,
  });
  const [liveAgentConfig, setLiveAgentConfig] = useState({
    global: {
      preview_timeout_seconds: 45,
      heartbeat_interval_seconds: 10,
      max_simultaneous_option_tickers: 48,
      delta_warning_threshold: 0.08,
      default_exit_wait_seconds: 5,
    },
    underlyings: {
      SPX: { strike_step: 0, delta_preview_initial_strike_limit_regular: 20, delta_preview_initial_strike_limit_small_delta: 36, delta_preview_fallback_strike_limit_regular: 120, delta_preview_fallback_strike_limit_small_delta: 160, price_preview_initial_strike_limit: 60, entry_price_adjustment_step: 0.05, entry_max_attempts: 3, fill_wait_seconds: 5 },
      SPY: { strike_step: 0, delta_preview_initial_strike_limit_regular: 20, delta_preview_initial_strike_limit_small_delta: 36, delta_preview_fallback_strike_limit_regular: 120, delta_preview_fallback_strike_limit_small_delta: 160, price_preview_initial_strike_limit: 24, entry_price_adjustment_step: 0.05, entry_max_attempts: 3, fill_wait_seconds: 5 },
      QQQ: { strike_step: 0, delta_preview_initial_strike_limit_regular: 20, delta_preview_initial_strike_limit_small_delta: 36, delta_preview_fallback_strike_limit_regular: 120, delta_preview_fallback_strike_limit_small_delta: 160, price_preview_initial_strike_limit: 24, entry_price_adjustment_step: 0.05, entry_max_attempts: 3, fill_wait_seconds: 5 },
      DIA: { strike_step: 1, delta_preview_initial_strike_limit_regular: 20, delta_preview_initial_strike_limit_small_delta: 36, delta_preview_fallback_strike_limit_regular: 120, delta_preview_fallback_strike_limit_small_delta: 160, price_preview_initial_strike_limit: 24, entry_price_adjustment_step: 0.05, entry_max_attempts: 3, fill_wait_seconds: 5 },
      DAX: { strike_step: 0, delta_preview_initial_strike_limit_regular: 20, delta_preview_initial_strike_limit_small_delta: 36, delta_preview_fallback_strike_limit_regular: 120, delta_preview_fallback_strike_limit_small_delta: 160, price_preview_initial_strike_limit: 24, entry_price_adjustment_step: 0.05, entry_max_attempts: 3, fill_wait_seconds: 30 },
    },
  });
  const [selectedLiveAgentUnderlying, setSelectedLiveAgentUnderlying] = useState('SPX');
  const [liveAgentConfigMsg, setLiveAgentConfigMsg] = useState('');
  const [liveAgentConfigErr, setLiveAgentConfigErr] = useState('');
  const [uiSettingsMsg, setUiSettingsMsg] = useState('');
  const [uiSettingsErr, setUiSettingsErr] = useState('');
  const [dataProviderSettings, setDataProviderSettings] = useState({ providers: [], modules: [], markets: [], settings: {} });
  const [dataProviderMappings, setDataProviderMappings] = useState([]);
  const [dataProviderMsg, setDataProviderMsg] = useState('');
  const [dataProviderErr, setDataProviderErr] = useState('');
  const [dataProviderTest, setDataProviderTest] = useState({
    module_key: 'rsl_scanner_daily',
    market_key: 'SP500',
    provider: 'configured',
  });
  const [dataProviderTestResult, setDataProviderTestResult] = useState(null);
  const [dataProviderTesting, setDataProviderTesting] = useState(false);
  const [smtpMsg, setSmtpMsg] = useState('');
  const [smtpErr, setSmtpErr] = useState('');
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterRecipients, setNewsletterRecipients] = useState([]);
  const [newsletterMsg, setNewsletterMsg] = useState('');
  const [newsletterErr, setNewsletterErr] = useState('');
  const [newsletterSending, setNewsletterSending] = useState(false);
  const [monitoring, setMonitoring] = useState({ generated_at: '', checks: [] });
  const [monitoringErr, setMonitoringErr] = useState('');
  const [deviceMonitorToken, setDeviceMonitorToken] = useState('');
  const [deviceMonitorStatusUrl, setDeviceMonitorStatusUrl] = useState('');
  const [deviceMonitorTokenMsg, setDeviceMonitorTokenMsg] = useState('');
  const [deviceMonitorTokenErr, setDeviceMonitorTokenErr] = useState('');
  const [deviceMonitorTokenGenerating, setDeviceMonitorTokenGenerating] = useState(false);
  const [meetingType, setMeetingType] = useState('coaching');
  const [meetingTopic, setMeetingTopic] = useState('');
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().slice(0, 10));
  const [meetingCopyMsg, setMeetingCopyMsg] = useState('');
  const meetingLink = useMemo(() => {
    const type = MEETING_TYPES.some(entry => entry.key === meetingType) ? meetingType : 'coaching';
    const topic = slugifyMeetingPart(meetingTopic);
    const datePart = slugifyMeetingPart(meetingDate);
    const fallback = datePart || new Date().toISOString().slice(0, 10);
    const codeParts = [type, topic || 'termin', fallback].filter(Boolean);
    return `${MEET_BASE_URL}/${codeParts.join('-')}`;
  }, [meetingDate, meetingTopic, meetingType]);
  const meetingFieldStyle = useMemo(() => ({
    ...inputStyle,
    width: '100%',
    height: '38px',
    minHeight: '38px',
    marginTop: '6px',
    marginBottom: 0,
    padding: '0 10px',
    lineHeight: '38px',
    boxSizing: 'border-box',
  }), [inputStyle]);
  const [securityEventsErr, setSecurityEventsErr] = useState('');
  const [watchlistKey, setWatchlistKey] = useState('hdax');
  const [watchlistFile, setWatchlistFile] = useState('');
  const [watchlistContent, setWatchlistContent] = useState('');
  const [watchlistMsg, setWatchlistMsg] = useState('');
  const [watchlistErr, setWatchlistErr] = useState('');
  const [backtestWatchlistKey, setBacktestWatchlistKey] = useState('hdax');
  const [backtestWatchlistFile, setBacktestWatchlistFile] = useState('');
  const [backtestWatchlistContent, setBacktestWatchlistContent] = useState('');
  const [backtestWatchlistVisibleContent, setBacktestWatchlistVisibleContent] = useState('');
  const [backtestWatchlistNullOnly, setBacktestWatchlistNullOnly] = useState(false);
  const [backtestWatchlistMsg, setBacktestWatchlistMsg] = useState('');
  const [backtestWatchlistErr, setBacktestWatchlistErr] = useState('');
  const [rslBacktestMode, setRslBacktestMode] = useState('next_daily_open');
  const [rslBacktestModeMsg, setRslBacktestModeMsg] = useState('');
  const [rslBacktestModeErr, setRslBacktestModeErr] = useState('');
  const [rslSignalSettings, setRslSignalSettings] = useState(DEFAULT_RSL_SIGNAL_SETTINGS);
  const [rslSignalSettingsMsg, setRslSignalSettingsMsg] = useState('');
  const [rslSignalSettingsErr, setRslSignalSettingsErr] = useState('');
  const [conversionRateFiles, setConversionRateFiles] = useState([]);
  const [conversionRateFile, setConversionRateFile] = useState(null);
  const [conversionRateMsg, setConversionRateMsg] = useState('');
  const [conversionRateErr, setConversionRateErr] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [userSort, setUserSort] = useState({ key: 'plan', direction: 'asc' });
  const [userBillingFilter, setUserBillingFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [userTotal, setUserTotal] = useState(0);
  const [userOffset, setUserOffset] = useState(0);
  const [userLimit] = useState(50);
  const [userExporting, setUserExporting] = useState(false);
  const [userExportErr, setUserExportErr] = useState('');
  const [openHandbookSections, setOpenHandbookSections] = useState(() => ({
    '📈 Strategien': false,
    '👥 User Verwaltung': false,
    '📣 Content Studio': false,
    '⚙ App Einstellungen': false,
    '💳 Billing': false,
    '🩺 App System': false,
    '🧩 Produkt-Pipeline': false,
    '📣 Newsletter': false,
    '🧭 Support-Logik im Alltag': false,
  }));
  const [reservedEmailMsg, setReservedEmailMsg] = useState('');
  const [reservedEmailErr, setReservedEmailErr] = useState('');
  const [form, setForm] = useState({
    email: '',
    password: '',
    is_admin: false,
    admin_notifications_enabled: true,
    trade_journal_order_form_enabled: false,
    access_until: '',
    plan: 'free',
    plan_status: 'active',
    trial_until: '',
    subscription_until: '',
  });
  const parseApiDate = (value) => {
    if (!value) return null;
    try {
      const normalizedValue = typeof value === 'string' && !/[zZ]|[+-]\d{2}:\d{2}$/.test(value)
        ? `${value}Z`
        : value;
      const parsed = new Date(normalizedValue);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  };
  const formatDateTime = (value) => {
    const parsed = parseApiDate(value);
    if (!parsed) return '—';
    return parsed.toLocaleString('de-DE');
  };
  const formatRelativeAge = (value) => {
    const parsed = parseApiDate(value);
    if (!parsed) return '—';
    const ageSeconds = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000));
    if (ageSeconds < 10) return 'gerade eben';
    if (ageSeconds < 60) return `vor ${ageSeconds}s`;
    const ageMinutes = Math.floor(ageSeconds / 60);
    if (ageMinutes < 60) return `vor ${ageMinutes}m`;
    const ageHours = Math.floor(ageMinutes / 60);
    if (ageHours < 48) return `vor ${ageHours}h`;
    return `vor ${Math.floor(ageHours / 24)}d`;
  };
  const formatDurationMs = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) return '—';
    if (numeric < 1000) return `${Math.round(numeric)} ms`;
    return `${(numeric / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;
  };
  const getLiveAgentBadgeColor = (color) => (
    ['green', 'yellow', 'red', 'ghost'].includes(color) ? color : 'ghost'
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (adminSection === 'users' && !usersMetaLoadedRef.current) {
      usersMetaLoadedRef.current = true;
      ladeUserStats();
      ladeReservedEmails();
      return;
    }

    if (adminSection === 'settings' && !settingsLoadedRef.current) {
      settingsLoadedRef.current = true;
      ladePlanModuleMatrix();
      ladeSmtpSettings();
      ladeUiSettings();
      return;
    }

    if (adminSection === 'data_providers' && !dataProvidersLoadedRef.current) {
      dataProvidersLoadedRef.current = true;
      ladeUiSettings();
      ladeDataProviderSettings();
      ladeDataProviderMappings();
      return;
    }

    if (adminSection === 'billing' && !billingLoadedRef.current) {
      billingLoadedRef.current = true;
      ladeBillingSettings();
      return;
    }

    if (adminSection === 'system' && !systemLoadedRef.current) {
      systemLoadedRef.current = true;
      ladeAuditLogs();
      ladeSecurityEvents();
      ladeMonitoring();
      return;
    }

    if (adminSection === 'strategies' && !strategiesLoadedRef.current) {
      strategiesLoadedRef.current = true;
      ladeWatchlist('hdax');
      ladeBacktestWatchlist('hdax');
      ladeRslBacktestMode();
      ladeRslSignalSettings();
      ladeConversionRates();
      return;
    }

    if (adminSection === 'product' && !productLoadedRef.current) {
      productLoadedRef.current = true;
      ladeChangelog();
      ladeFeatures();
      ladeRoadmap();
      return;
    }

    if (adminSection === 'live_agents' && !liveAgentsLoadedRef.current) {
      liveAgentsLoadedRef.current = true;
      ladeLiveAgents();
      return;
    }

    if (adminSection === 'live_agent_setup' && !liveAgentSetupLoadedRef.current) {
      liveAgentSetupLoadedRef.current = true;
      ladeLiveAgentConfig();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSection]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (adminSection !== 'users') return;
    ladeUsers(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSection, userSearch, userBillingFilter, userSort]);

  function handleError(err) {
    if (err instanceof ApiError && err.status === 401) {
      onSessionExpired();
    }
  }

  const ladeIndexCheckReminder = useCallback(async () => {
    setIndexCheckReminderErr('');
    try {
      const data = await apiRequest('/admin/reminders/index-check');
      setIndexCheckReminder({
        should_show: !!data?.should_show,
        message: String(data?.message || 'Indizes auf Änderungen prüfen'),
        period_key: String(data?.period_key || ''),
        acknowledged: !!data?.acknowledged,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      setIndexCheckReminderErr(err?.message || 'Admin-Erinnerung konnte nicht geladen werden');
    }
  }, [onSessionExpired]);

  async function bestaetigeIndexCheckReminder() {
    setIndexCheckReminderErr('');
    setIndexCheckReminderSaving(true);
    try {
      await apiRequest('/admin/reminders/index-check/ack', { method: 'POST' });
      setIndexCheckReminder(current => ({ ...current, should_show: false, acknowledged: true }));
      onToast?.('Erinnerung bestätigt', 'success');
    } catch (err) {
      handleError(err);
      setIndexCheckReminderErr(err?.message || 'Erinnerung konnte nicht bestätigt werden');
    } finally {
      setIndexCheckReminderSaving(false);
    }
  }

  const currentLiveAgentUnderlyingConfig = liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying] || {};
  const currentLiveAgentGlobalConfig = liveAgentConfig.global || {};

  function updateLiveAgentUnderlyingConfig(field, value) {
    setLiveAgentConfig(current => ({
      ...current,
      underlyings: {
        ...(current.underlyings || {}),
        [selectedLiveAgentUnderlying]: {
          ...(current.underlyings?.[selectedLiveAgentUnderlying] || {}),
          [field]: value,
        },
      },
    }));
  }

  function updateLiveAgentGlobalConfig(field, value) {
    setLiveAgentConfig(current => ({
      ...current,
      global: {
        ...(current.global || {}),
        [field]: value,
      },
    }));
  }

  const ladeLiveAgents = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setLiveAgentsErr('');
    try {
      const data = await apiRequest('/admin/live-agents');
      const items = Array.isArray(data?.items) ? data.items : [];
      setLiveAgents(items);
      setLiveAgentsUpdatedAt(new Date().toISOString());
      setSelectedLiveAgentKey(currentKey => (
        currentKey && items.some(item => `${item.account_id}-${item.agent_email}` === currentKey)
          ? currentKey
          : ''
      ));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      if (!silent) {
        setLiveAgentsErr(err?.message || 'Live-Agents konnten nicht geladen werden');
      }
    }
  }, [onSessionExpired]);

  async function copyLiveAgentText(text) {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setLiveAgentCopyMsg('In Zwischenablage kopiert');
    } catch {
      setLiveAgentCopyMsg('Kopieren nicht möglich');
    }
    window.setTimeout(() => setLiveAgentCopyMsg(''), 2200);
  }

  useEffect(() => {
    if (adminSection !== 'live_agents') return undefined;
    const intervalId = window.setInterval(() => {
      ladeLiveAgents({ silent: true });
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [adminSection, ladeLiveAgents]);

  useEffect(() => {
    ladeIndexCheckReminder();
  }, [ladeIndexCheckReminder]);

  async function ladeUsers(nextOffset = userOffset) {
    try {
      const params = new URLSearchParams({
        limit: String(userLimit),
        offset: String(Math.max(0, nextOffset)),
        search: userSearch.trim(),
        billing_filter: userBillingFilter,
        sort_key: userSort.key,
        sort_direction: userSort.direction,
      });
      const data = await apiRequest(`/admin/users?${params.toString()}`);
      setUsers(Array.isArray(data?.items) ? data.items : []);
      setUserTotal(Number.isFinite(data?.total) ? data.total : 0);
      setUserOffset(Number.isFinite(data?.offset) ? data.offset : Math.max(0, nextOffset));
    } catch (err) {
      handleError(err);
    }
  }

  function toggleUserSort(sortKey) {
    setUserSort(current => (
      current.key === sortKey
        ? { key: sortKey, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key: sortKey, direction: 'asc' }
    ));
  }

  async function exportUsersCsv() {
    setUserExportErr('');
    setUserExporting(true);
    try {
      const params = new URLSearchParams({
        search: userSearch.trim(),
        billing_filter: userBillingFilter,
        sort_key: userSort.key,
        sort_direction: userSort.direction,
      });
      const blob = await apiBlobRequest(`/admin/users/export?${params.toString()}`, { timeoutMs: 60000 });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `admin-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      handleError(err);
      setUserExportErr(err?.message || 'User-CSV konnte nicht exportiert werden');
    } finally {
      setUserExporting(false);
    }
  }

  function toggleHandbookSection(title) {
    setOpenHandbookSections(current => ({
      ...current,
      [title]: !current[title],
    }));
  }

  const visibleUsers = users;
  const visibleRangeStart = userTotal === 0 ? 0 : userOffset + 1;
  const visibleRangeEnd = userTotal === 0 ? 0 : Math.min(userOffset + visibleUsers.length, userTotal);
  const canGoUserBack = userOffset > 0;
  const canGoUserForward = userOffset + userLimit < userTotal;

  async function ladeUserStats() {
    setUserStatsErr('');
    try {
      const data = await apiRequest('/admin/user-stats');
      setUserStats({
        total_users: data.total_users || 0,
        active_users: data.active_users || 0,
        verified_users: data.verified_users || 0,
        admins: data.admins || 0,
        free_users: data.free_users || 0,
        basic_users: data.basic_users || 0,
        premium_users: data.premium_users || 0,
        trial_users: data.trial_users || 0,
        recent_logins_24h: data.recent_logins_24h || 0,
        recent_logins_7d: data.recent_logins_7d || 0,
      });
    } catch (err) {
      handleError(err);
      setUserStatsErr(err?.message || 'User-Statistik konnte nicht geladen werden');
    }
  }

  async function ladeAuditLogs() {
    try {
      const data = await apiRequest('/admin/audit-logs?limit=50');
      setAuditLogs(data);
    } catch (err) {
      handleError(err);
    }
  }

  async function ladeConversionRates() {
    setConversionRateErr('');
    try {
      const data = await apiRequest('/admin/settings/conversion-rates');
      setConversionRateFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      handleError(err);
      setConversionRateErr(err?.message || 'ConversionRates konnten nicht geladen werden');
    }
  }

  async function ladeSecurityEvents() {
    setSecurityEventsErr('');
    try {
      const data = await apiRequest('/admin/security-events?limit=100');
      setSecurityEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      handleError(err);
      setSecurityEventsErr(err?.message || 'Security-Events konnten nicht geladen werden');
    }
  }

  async function ladeReservedEmails() {
    setReservedEmailErr('');
    setReservedEmailMsg('');
    try {
      const data = await apiRequest('/admin/reserved-emails');
      setReservedEmails(Array.isArray(data) ? data : []);
    } catch (err) {
      handleError(err);
      setReservedEmailErr(err?.message || 'Reservierte E-Mails konnten nicht geladen werden');
    }
  }

  async function ladeSmtpSettings() {
    try {
      const data = await apiRequest('/admin/settings/smtp');
      setSmtp(s => ({
        ...s,
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 587,
        smtp_user: data.smtp_user || '',
        smtp_from: data.smtp_from || '',
        smtp_tls: !!data.smtp_tls,
        smtp_pass: '',
        smtp_pass_set: !!data.smtp_pass_set,
        signals_notify_premium: data.signals_notify_premium !== false,
      }));
    } catch (err) {
      handleError(err);
    }
  }

  async function ladeBillingSettings() {
    setBillingErr('');
    setBillingMsg('');
    try {
      const data = await apiRequest('/admin/settings/billing');
      setBilling(s => ({
        ...s,
        billing_enabled: !!data.billing_enabled,
        billing_provider: data.billing_provider || 'stripe',
        billing_public_key: data.billing_public_key || '',
        billing_secret_key: '',
        billing_secret_key_set: !!data.billing_secret_key_set,
        billing_webhook_secret: '',
        billing_webhook_secret_set: !!data.billing_webhook_secret_set,
        billing_portal_enabled: !!data.billing_portal_enabled,
        billing_success_url: data.billing_success_url || '',
        billing_cancel_url: data.billing_cancel_url || '',
        basic_monthly_enabled: data.basic_monthly_enabled !== false,
        basic_yearly_enabled: data.basic_yearly_enabled !== false,
        premium_monthly_enabled: data.premium_monthly_enabled !== false,
        premium_yearly_enabled: data.premium_yearly_enabled !== false,
        coaching_enabled: data.coaching_enabled !== false,
        stripe_price_basic_monthly: data.stripe_price_basic_monthly || '',
        stripe_price_basic_yearly: data.stripe_price_basic_yearly || '',
        stripe_price_premium_monthly: data.stripe_price_premium_monthly || '',
        stripe_price_premium_yearly: data.stripe_price_premium_yearly || '',
        stripe_price_coaching: data.stripe_price_coaching || '',
      }));
    } catch (err) {
      handleError(err);
      setBillingErr(err?.message || 'Billing-Einstellungen konnten nicht geladen werden');
    }
  }

  async function ladeUiSettings() {
    setUiSettingsErr('');
    setUiSettingsMsg('');
    try {
      const data = await apiRequest('/admin/settings/ui');
      setUiSettings({
        tradingview_charts_enabled: !!data.tradingview_charts_enabled,
        marketstack_api_key: '',
        marketstack_api_key_set: !!data.marketstack_api_key_set,
        clear_marketstack_api_key: false,
      });
    } catch (err) {
      handleError(err);
      setUiSettingsErr(err?.message || 'UI-Einstellungen konnten nicht geladen werden');
    }
  }

  async function ladeDataProviderSettings() {
    setDataProviderErr('');
    setDataProviderMsg('');
    try {
      const data = await apiRequest('/admin/settings/data-providers');
      setDataProviderSettings({
        providers: Array.isArray(data.providers) ? data.providers : [],
        modules: Array.isArray(data.modules) ? data.modules : [],
        markets: Array.isArray(data.markets) ? data.markets : [],
        settings: data.settings || {},
      });
      if (!dataProviderTest.module_key && Array.isArray(data.modules) && data.modules[0]?.key) {
        setDataProviderTest(test => ({ ...test, module_key: data.modules[0].key }));
      }
    } catch (err) {
      handleError(err);
      setDataProviderErr(err?.message || 'Datenanbieter konnten nicht geladen werden');
    }
  }

  async function ladeDataProviderMappings() {
    setDataProviderErr('');
    try {
      const data = await apiRequest('/admin/settings/data-provider-mappings');
      setDataProviderMappings(Array.isArray(data.mappings) ? data.mappings : []);
    } catch (err) {
      handleError(err);
      setDataProviderErr(err?.message || 'Ticker-Mapping konnte nicht geladen werden');
    }
  }

  async function ladeLiveAgentConfig() {
    setLiveAgentConfigErr('');
    setLiveAgentConfigMsg('');
    try {
      const data = await apiRequest('/admin/settings/live-agent-config');
      setLiveAgentConfig(s => ({
        ...s,
        ...data,
        global: { ...(s.global || {}), ...(data.global || {}) },
        underlyings: { ...(s.underlyings || {}), ...(data.underlyings || {}) },
      }));
    } catch (err) {
      handleError(err);
      setLiveAgentConfigErr(err?.message || 'Live-Agent-Konfiguration konnte nicht geladen werden');
    }
  }

  async function ladeChangelog() {
    setChangelogErr('');
    setChangelogMsg('');
    try {
      const data = await apiRequest('/admin/settings/changelog');
      setChangelogRevision(Number(data.revision || 0));
      setChangelogEntries(
        Array.isArray(data.entries) && data.entries.length > 0 ? data.entries : [{ entry_date: '', change: '' }]
      );
    } catch (err) {
      handleError(err);
      setChangelogErr(err?.message || 'Changelog konnte nicht geladen werden');
    }
  }

  async function ladeFeatures() {
    setFeatureErr('');
    setFeatureMsg('');
    try {
      const data = await apiRequest('/admin/settings/features');
      setFeatureRevision(Number(data.revision || 0));
      setFeatureEntries(
        Array.isArray(data.entries) && data.entries.length > 0
          ? data.entries.map(entry => ({ ...entry, priority: entry.priority || 'mittel' }))
          : [{ id: '', title: '', description: '', priority: 'mittel' }]
      );
    } catch (err) {
      handleError(err);
      setFeatureErr(err?.message || 'Feature-Liste konnte nicht geladen werden');
    }
  }

  async function ladeRoadmap() {
    setRoadmapErr('');
    setRoadmapMsg('');
    try {
      const data = await apiRequest('/admin/settings/roadmap');
      setRoadmapRevision(Number(data.revision || 0));
      setRoadmapEntries(
        Array.isArray(data.entries) && data.entries.length > 0
          ? data.entries.map(entry => ({ ...entry, status: normalizeRoadmapStatus(entry.status) }))
          : [{ title: '', description: '', status: 'geplant', target: '', show_in_changelog: false, changelog_date: '' }]
      );
    } catch (err) {
      handleError(err);
      setRoadmapErr(err?.message || 'Roadmap konnte nicht geladen werden');
    }
  }

  async function ladePlanModuleMatrix() {
    setPlanModuleMatrixErr('');
    setPlanModuleMatrixMsg('');
    try {
      const data = await apiRequest('/admin/settings/plan-module-matrix');
      setPlanModuleMatrixRevision(Number(data.revision || 0));
      setPlanModuleMatrixEntries(
        PLAN_MODULE_MATRIX_ROWS.map(row => {
          const loaded = Array.isArray(data.entries)
            ? data.entries.find(entry => entry.module === row.module)
            : null;
          return {
            ...row,
            trial: !!loaded?.trial,
            basic: !!loaded?.basic,
            premium: !!loaded?.premium,
          };
        })
      );
    } catch (err) {
      handleError(err);
      setPlanModuleMatrixErr(err?.message || 'Rechte-Matrix konnte nicht geladen werden');
    }
  }

  async function ladeMonitoring() {
    setMonitoringErr('');
    try {
      const data = await apiRequest('/admin/monitoring');
      setMonitoring({
        generated_at: data.generated_at || '',
        checks: Array.isArray(data.checks) ? data.checks : [],
      });
    } catch (err) {
      handleError(err);
      setMonitoringErr(err?.message || 'Monitoring konnte nicht geladen werden');
    }
  }

  async function generateDeviceMonitorToken() {
    setDeviceMonitorTokenErr('');
    setDeviceMonitorTokenMsg('');
    setDeviceMonitorTokenGenerating(true);
    try {
      const data = await apiRequest('/admin/monitoring/device-token', { method: 'POST' });
      const token = String(data?.token || '');
      const statusUrl = String(data?.status_url || '');
      setDeviceMonitorToken(token);
      setDeviceMonitorStatusUrl(statusUrl);
      setDeviceMonitorTokenMsg('Token erzeugt. Nur jetzt vollständig sichtbar.');
      if (token && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(token);
          setDeviceMonitorTokenMsg('Token erzeugt und kopiert. Nur jetzt vollständig sichtbar.');
        } catch {
          // Clipboard can be blocked by browser permissions; the token remains visible.
        }
      }
    } catch (err) {
      handleError(err);
      setDeviceMonitorTokenErr(err?.message || 'Device-Monitor-Token konnte nicht erzeugt werden');
    } finally {
      setDeviceMonitorTokenGenerating(false);
    }
  }

  async function copyDeviceMonitorValue(value) {
    if (!value) return;
    setDeviceMonitorTokenErr('');
    try {
      await navigator.clipboard.writeText(value);
      setDeviceMonitorTokenMsg('Kopiert');
    } catch {
      setDeviceMonitorTokenErr('Kopieren nicht möglich');
    }
  }

  async function copyMeetingText(value, label = 'Meeting-Link') {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setMeetingCopyMsg(`${label} kopiert`);
    } catch {
      setMeetingCopyMsg('Kopieren nicht möglich');
    }
    window.setTimeout(() => setMeetingCopyMsg(''), 2200);
  }

  async function speichernSmtp() {
    setSmtpErr('');
    setSmtpMsg('');
    try {
      await apiRequest('/admin/settings/smtp', {
        method: 'PATCH',
        body: {
          smtp_host: smtp.smtp_host,
          smtp_port: Number(smtp.smtp_port || 587),
          smtp_user: smtp.smtp_user,
          smtp_from: smtp.smtp_from,
          smtp_tls: !!smtp.smtp_tls,
          signals_notify_premium: !!smtp.signals_notify_premium,
          ...(smtp.smtp_pass ? { smtp_pass: smtp.smtp_pass } : {}),
        },
      });
      setSmtpMsg('SMTP-Einstellungen gespeichert');
      setSmtp(s => ({ ...s, smtp_pass: '' }));
      ladeSmtpSettings();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setSmtpErr(err?.message || 'Speichern fehlgeschlagen');
    }
  }

  async function speichernBilling() {
    setBillingErr('');
    setBillingMsg('');
    try {
      await apiRequest('/admin/settings/billing', {
        method: 'PATCH',
        body: {
          billing_enabled: !!billing.billing_enabled,
          billing_provider: billing.billing_provider || 'stripe',
          billing_public_key: billing.billing_public_key || '',
          billing_secret_key: billing.billing_secret_key || undefined,
          billing_webhook_secret: billing.billing_webhook_secret || undefined,
          billing_portal_enabled: !!billing.billing_portal_enabled,
          billing_success_url: billing.billing_success_url || '',
          billing_cancel_url: billing.billing_cancel_url || '',
          basic_monthly_enabled: !!billing.basic_monthly_enabled,
          basic_yearly_enabled: !!billing.basic_yearly_enabled,
          premium_monthly_enabled: !!billing.premium_monthly_enabled,
          premium_yearly_enabled: !!billing.premium_yearly_enabled,
          coaching_enabled: !!billing.coaching_enabled,
          stripe_price_basic_monthly: billing.stripe_price_basic_monthly || '',
          stripe_price_basic_yearly: billing.stripe_price_basic_yearly || '',
          stripe_price_premium_monthly: billing.stripe_price_premium_monthly || '',
          stripe_price_premium_yearly: billing.stripe_price_premium_yearly || '',
          stripe_price_coaching: billing.stripe_price_coaching || '',
        },
      });
      setBillingMsg('Billing-Einstellungen gespeichert');
      ladeBillingSettings();
    } catch (err) {
      handleError(err);
      setBillingErr(err?.message || 'Billing-Einstellungen konnten nicht gespeichert werden');
    }
  }

  async function importiereBillingHistorie() {
    setBillingInvoiceExportErr('');
    setBillingInvoiceExportMsg('');
    setBillingImporting(true);
    try {
      const data = await apiRequest('/admin/billing/import-history', {
        method: 'POST',
        body: {},
      });
      const skippedInfo = data.skipped_count ? ` ${data.skipped_count} Kund:innen wurden übersprungen, weil Stripe sie mit dem aktuellen API-Key nicht laden konnte.` : '';
      setBillingInvoiceExportMsg(`Stripe-Historie importiert: ${data.imported_count} neue Einträge aus ${data.invoice_count} Rechnungen bei ${data.users_scanned} Kund:innen.${skippedInfo}`);
    } catch (err) {
      handleError(err);
      setBillingInvoiceExportErr(err?.message || 'Billing-Historie konnte nicht importiert werden');
    } finally {
      setBillingImporting(false);
    }
  }

  async function ladeBillingBelegeZip() {
    setBillingInvoiceExportErr('');
    setBillingInvoiceExportMsg('');
    setBillingInvoiceExportLoading(true);
    try {
      const query = new URLSearchParams({
        year: String(billingInvoiceExport.year),
        month: String(billingInvoiceExport.month),
        status: 'all',
        document_type: 'both',
      });
      const blob = await apiBlobRequest(`/admin/billing/invoices/download?${query.toString()}`, {
        timeoutMs: 120000,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `billing-belege-${billingInvoiceExport.year}-${String(billingInvoiceExport.month).padStart(2, '0')}-alle.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setBillingInvoiceExportMsg('Beleg-ZIP wurde erstellt und heruntergeladen.');
    } catch (err) {
      handleError(err);
      setBillingInvoiceExportErr(err?.message || 'Beleg-ZIP konnte nicht erstellt werden');
    } finally {
      setBillingInvoiceExportLoading(false);
    }
  }

  async function ladeUserBillingHistory(user) {
    setSelectedBillingHistory([]);
    setSelectedBillingHistoryErr('');
    setSelectedBillingHistoryLoading(true);
    try {
      const data = await apiRequest(`/admin/users/${user.id}/billing-history`);
      setSelectedBillingHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      handleError(err);
      setSelectedBillingHistoryErr(err?.message || 'Billing-Historie konnte nicht geladen werden');
    } finally {
      setSelectedBillingHistoryLoading(false);
    }
  }

  async function toggleUserBillingHistory(user) {
    if (selectedBillingUser?.id === user.id) {
      setSelectedBillingUser(null);
      setSelectedBillingHistory([]);
      setSelectedBillingHistoryErr('');
      return;
    }
    setSelectedBillingUser(user);
    await ladeUserBillingHistory(user);
  }

  function formatBillingAmount(amountTotal, currency) {
    if (!Number.isFinite(amountTotal)) return '—';
    const normalizedCurrency = (currency || 'EUR').toUpperCase();
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: normalizedCurrency }).format(amountTotal / 100);
    } catch {
      return `${(amountTotal / 100).toFixed(2)} ${normalizedCurrency}`;
    }
  }

  function formatBillingProductLabel(entry) {
    switch ((entry?.product_key || '').toLowerCase()) {
      case 'basic_monthly':
      case 'basic_yearly':
      case 'essential':
      case 'basic':
        return 'Essential';
      case 'premium_monthly':
      case 'premium_yearly':
      case 'premium':
        return 'Plus';
      case 'coaching':
        return 'Anleitung';
      default:
        return 'Mitgliedschaft';
    }
  }

  function formatBillingStatusLabel(status) {
    switch (String(status || 'none').toLowerCase()) {
      case 'active':
        return 'aktiv';
      case 'trialing':
        return 'Testphase';
      case 'incomplete':
        return 'offen';
      case 'past_due':
        return 'überfällig';
      case 'unpaid':
        return 'unbezahlt';
      case 'canceled':
        return 'gekündigt';
      default:
        return 'keins';
    }
  }

  function getBillingStatusBadgeColor(status) {
    switch (String(status || 'none').toLowerCase()) {
      case 'active':
        return 'green';
      case 'trialing':
        return 'cyan';
      case 'incomplete':
        return 'yellow';
      case 'past_due':
      case 'unpaid':
        return 'red';
      case 'canceled':
        return 'ghost';
      default:
        return 'ghost';
    }
  }

  function getBillingHistoryBadgeColor(status) {
    if (status === 'paid') return 'green';
    if (status === 'payment_failed') return 'red';
    if (status === 'pending') return 'yellow';
    return 'ghost';
  }

  async function syncUserBillingHistory(user) {
    try {
      const result = await apiRequest(`/admin/users/${user.id}/billing-sync`, {
        method: 'POST',
        body: {},
      });
      onToast?.(
        `Stripe-Sync für ${user.email}: ${result.imported_count} neue Einträge aus ${result.invoice_count} Rechnungen.`,
        'success'
      );
      await Promise.all([ladeUsers(), ladeUserStats(), ladeAuditLogs()]);
      if (selectedBillingUser?.id === user.id) {
        await ladeUserBillingHistory(user);
      }
    } catch (err) {
      handleError(err);
      onToast?.(err?.message || 'Stripe-Sync fehlgeschlagen', 'error');
    }
  }

  async function speichernUiSettings() {
    setUiSettingsErr('');
    setUiSettingsMsg('');
    try {
      await apiRequest('/admin/settings/ui', {
        method: 'PATCH',
        body: {
          tradingview_charts_enabled: !!uiSettings.tradingview_charts_enabled,
          marketstack_api_key: uiSettings.marketstack_api_key || undefined,
          clear_marketstack_api_key: !!uiSettings.clear_marketstack_api_key,
        },
      });
      setUiSettingsMsg('App-Einstellungen gespeichert');
      ladeUiSettings();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setUiSettingsErr(err?.message || 'Chart-Einstellungen konnten nicht gespeichert werden');
    }
  }

  function updateDataProviderSetting(moduleKey, marketKey, field, value) {
    setDataProviderSettings(current => ({
      ...current,
      settings: {
        ...(current.settings || {}),
        [moduleKey]: {
          ...((current.settings || {})[moduleKey] || {}),
          [marketKey]: {
            ...(((current.settings || {})[moduleKey] || {})[marketKey] || { primary_provider: 'yahoo', fallback_provider: 'none' }),
            [field]: value,
          },
        },
      },
    }));
  }

  async function speichernDataProviderSettings() {
    setDataProviderErr('');
    setDataProviderMsg('');
    try {
      const data = await apiRequest('/admin/settings/data-providers', {
        method: 'PATCH',
        body: { settings: dataProviderSettings.settings || {} },
      });
      setDataProviderSettings({
        providers: Array.isArray(data.providers) ? data.providers : [],
        modules: Array.isArray(data.modules) ? data.modules : [],
        markets: Array.isArray(data.markets) ? data.markets : [],
        settings: data.settings || {},
      });
      setDataProviderMsg('Datenanbieter-Konfiguration gespeichert');
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setDataProviderErr(err?.message || 'Datenanbieter-Konfiguration konnte nicht gespeichert werden');
    }
  }

  function updateDataProviderMapping(index, field, value) {
    setDataProviderMappings(rows => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }

  function addDataProviderMapping() {
    setDataProviderMappings(rows => [
      ...rows,
      { scope: 'GLOBAL', app_symbol: '', yahoo_symbol: '', marketstack_symbol: '', tradingview_symbol: '', active: true, note: '' },
    ]);
  }

  function removeDataProviderMapping(index) {
    setDataProviderMappings(rows => rows.filter((_, i) => i !== index));
  }

  async function speichernDataProviderMappings() {
    setDataProviderErr('');
    setDataProviderMsg('');
    try {
      const data = await apiRequest('/admin/settings/data-provider-mappings', {
        method: 'PATCH',
        body: { mappings: dataProviderMappings },
      });
      setDataProviderMappings(Array.isArray(data.mappings) ? data.mappings : []);
      setDataProviderMsg('Ticker-Mapping gespeichert');
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setDataProviderErr(err?.message || 'Ticker-Mapping konnte nicht gespeichert werden');
    }
  }

  async function testeDataProvider() {
    setDataProviderErr('');
    setDataProviderMsg('');
    setDataProviderTesting(true);
    setDataProviderTestResult(null);
    try {
      const data = await apiRequest('/admin/settings/data-providers/test', {
        method: 'POST',
        body: dataProviderTest,
      });
      setDataProviderTestResult(data);
      setDataProviderMsg(data.message || 'Datenanbieter-Test abgeschlossen');
    } catch (err) {
      handleError(err);
      setDataProviderErr(err?.message || 'Datenanbieter-Test fehlgeschlagen');
    } finally {
      setDataProviderTesting(false);
    }
  }

  async function speichernLiveAgentConfig() {
    setLiveAgentConfigErr('');
    setLiveAgentConfigMsg('');
    try {
      await apiRequest('/admin/settings/live-agent-config', {
        method: 'PATCH',
        body: {
          global: {
            preview_timeout_seconds: Number(liveAgentConfig.global?.preview_timeout_seconds || 45),
            heartbeat_interval_seconds: Number(liveAgentConfig.global?.heartbeat_interval_seconds || 10),
            max_simultaneous_option_tickers: Number(liveAgentConfig.global?.max_simultaneous_option_tickers || 48),
            delta_warning_threshold: Number(liveAgentConfig.global?.delta_warning_threshold || 0.08),
            default_exit_wait_seconds: Number(liveAgentConfig.global?.default_exit_wait_seconds || 5),
          },
          underlyings: {
            [selectedLiveAgentUnderlying]: {
              strike_step: Number(liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying]?.strike_step || 0),
              delta_preview_initial_strike_limit_regular: Number(liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying]?.delta_preview_initial_strike_limit_regular || 20),
              delta_preview_initial_strike_limit_small_delta: Number(liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying]?.delta_preview_initial_strike_limit_small_delta || 36),
              delta_preview_fallback_strike_limit_regular: Number(liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying]?.delta_preview_fallback_strike_limit_regular || 120),
              delta_preview_fallback_strike_limit_small_delta: Number(liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying]?.delta_preview_fallback_strike_limit_small_delta || 160),
              price_preview_initial_strike_limit: Number(liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying]?.price_preview_initial_strike_limit || 24),
              entry_price_adjustment_step: Number(liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying]?.entry_price_adjustment_step || 0.05),
              entry_max_attempts: Number(liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying]?.entry_max_attempts || 3),
              fill_wait_seconds: Number(liveAgentConfig.underlyings?.[selectedLiveAgentUnderlying]?.fill_wait_seconds || 5),
            },
          },
        },
      });
      setLiveAgentConfigMsg(`Live-Agent-Konfiguration fuer ${selectedLiveAgentUnderlying} gespeichert`);
      ladeLiveAgentConfig();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setLiveAgentConfigErr(err?.message || 'Live-Agent-Konfiguration konnte nicht gespeichert werden');
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function speichernChangelog() {
    setChangelogErr('');
    setChangelogMsg('');
    const entries = changelogEntries
      .map(entry => ({
        entry_date: entry.entry_date,
        change: (entry.change || '').trim(),
      }))
      .filter(entry => entry.entry_date && entry.change);

    try {
      await apiRequest('/admin/settings/changelog', {
        method: 'PUT',
        body: { revision: changelogRevision, entries },
      });
      setChangelogMsg('Changelog gespeichert');
      ladeChangelog();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setChangelogErr(err?.message || 'Changelog konnte nicht gespeichert werden');
    }
  }

  async function speichernFeatures() {
    setFeatureErr('');
    setFeatureMsg('');
    const entries = featureEntries
      .map(entry => ({
        id: entry.id || undefined,
        title: (entry.title || '').trim(),
        description: (entry.description || '').trim(),
        priority: entry.priority || 'mittel',
      }));

    const invalidEntry = entries.find(entry => {
      if (!entry.title && !entry.description) return false;
      if (!entry.title) return true;
      if (!entry.description) return true;
      if (entry.title.length < 3) return true;
      if (entry.description.length < 3) return true;
      return false;
    });

    if (invalidEntry) {
      if (!invalidEntry.title) {
        setFeatureErr('Produkt-Pipeline: Titel fehlt');
      } else if (!invalidEntry.description) {
        setFeatureErr(`Produkt-Pipeline: Beschreibung für "${invalidEntry.title}" fehlt`);
      } else if (invalidEntry.title.length < 3) {
        setFeatureErr(`Produkt-Pipeline: Titel "${invalidEntry.title}" ist zu kurz`);
      } else {
        setFeatureErr(`Produkt-Pipeline: Beschreibung für "${invalidEntry.title}" ist zu kurz`);
      }
      return;
    }

    const cleanedEntries = entries.filter(entry => entry.title && entry.description);
    try {
      await apiRequest('/admin/settings/features', {
        method: 'PUT',
        body: { revision: featureRevision, entries: cleanedEntries },
      });
      setFeatureMsg('Feature-Liste gespeichert');
      ladeFeatures();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setFeatureErr(err?.message || 'Feature-Liste konnte nicht gespeichert werden');
    }
  }

  async function speichernRoadmap() {
    setRoadmapErr('');
    setRoadmapMsg('');
    const entries = roadmapEntries
      .map(entry => ({
        title: (entry.title || '').trim(),
        description: (entry.description || '').trim(),
        status: normalizeRoadmapStatus(entry.status),
        target: (entry.target || '').trim(),
        show_in_changelog: !!entry.show_in_changelog,
        changelog_date: entry.show_in_changelog && entry.changelog_date ? entry.changelog_date : null,
      }))
      .filter(entry => entry.title && entry.description);
    try {
      await apiRequest('/admin/settings/roadmap', {
        method: 'PUT',
        body: { revision: roadmapRevision, entries },
      });
      setRoadmapMsg('Roadmap gespeichert');
      ladeRoadmap();
      ladeChangelog();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setRoadmapErr(err?.message || 'Roadmap konnte nicht gespeichert werden');
    }
  }

  async function speichernPlanModuleMatrix() {
    setPlanModuleMatrixErr('');
    setPlanModuleMatrixMsg('');
    try {
      await apiRequest('/admin/settings/plan-module-matrix', {
        method: 'PUT',
        body: {
          revision: planModuleMatrixRevision,
          entries: planModuleMatrixEntries.map(entry => ({
            module: entry.module,
            label: entry.label,
            trial: !!entry.trial,
            basic: !!entry.basic,
            premium: !!entry.premium,
          })),
        },
      });
      setPlanModuleMatrixMsg('Rechte-Matrix gespeichert');
      ladePlanModuleMatrix();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setPlanModuleMatrixErr(err?.message || 'Rechte-Matrix konnte nicht gespeichert werden');
    }
  }

  function updatePlanModuleMatrixEntry(moduleName, field, value) {
    setPlanModuleMatrixEntries(entries => entries.map(entry => (
      entry.module === moduleName ? { ...entry, [field]: value } : entry
    )));
  }

  function addFeatureEntry() {
    setFeatureEntries(entries => [...entries, { id: '', title: '', description: '', priority: 'mittel' }]);
  }

  function updateFeatureEntry(index, field, value) {
    setFeatureEntries(entries => entries.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  }

  function removeFeatureEntry(index) {
    setFeatureEntries(entries => {
      const next = entries.filter((_, i) => i !== index);
      return next.length > 0 ? next : [{ id: '', title: '', description: '', priority: 'mittel' }];
    });
  }

  function addFeatureToRoadmap(entry) {
    if (!entry?.title || !entry?.description) return;
    const exists = roadmapEntries.some(
      item => item.title.trim().toLowerCase() === entry.title.trim().toLowerCase()
    );
    if (exists) {
      setRoadmapMsg(`Feature "${entry.title}" ist bereits in der Roadmap.`);
      setRoadmapErr('');
      return;
    }
    setRoadmapEntries(entries => [
      {
        title: entry.title,
        description: entry.description,
        status: 'geplant',
        target: '',
        show_in_changelog: false,
        changelog_date: '',
      },
      ...entries,
    ]);
    setRoadmapMsg(`Feature "${entry.title}" in die Roadmap übernommen.`);
    setRoadmapErr('');
  }

  function updateRoadmapEntry(index, field, value) {
    setRoadmapEntries(entries => entries.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  }

  function removeRoadmapEntry(index) {
    setRoadmapEntries(entries => {
      const next = entries.filter((_, i) => i !== index);
      return next.length > 0 ? next : [{ title: '', description: '', status: 'geplant', target: '', show_in_changelog: false, changelog_date: '' }];
    });
  }

  function toggleRoadmapFilter(statusKey) {
    setRoadmapFilters(current => ({ ...current, [statusKey]: !current[statusKey] }));
  }

  async function testSmtp() {
    setSmtpErr('');
    setSmtpMsg('');
    if (!smtpTestEmail.trim()) {
      setSmtpErr('Bitte Test-E-Mail-Adresse eingeben');
      return;
    }

    try {
      const res = await apiRequest('/admin/settings/smtp/test', {
        method: 'POST',
        body: { to_email: smtpTestEmail.trim() },
      });
      setSmtpMsg(res.message || 'Test-E-Mail wurde ausgelöst');
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setSmtpErr(err?.message || 'Test-E-Mail fehlgeschlagen');
    }
  }

  function toggleNewsletterRecipient(email) {
    setNewsletterRecipients(current => (
      current.includes(email)
        ? current.filter(item => item !== email)
        : [...current, email]
    ));
  }

  function selectAllActiveNewsletterRecipients() {
    setNewsletterRecipients(
      users
        .filter(user => user.is_active)
        .map(user => user.email)
    );
  }

  function selectPremiumNewsletterRecipients() {
    setNewsletterRecipients(
      users
        .filter(user => user.is_active && (user.is_admin || user.effective_plan === 'premium'))
        .map(user => user.email)
    );
  }

  async function sendNewsletter() {
    setNewsletterErr('');
    setNewsletterMsg('');
    setNewsletterSending(true);
    try {
      const result = await apiRequest('/admin/newsletter/send', {
        method: 'POST',
        body: {
          subject: newsletterSubject,
          message: newsletterMessage,
          recipient_emails: newsletterRecipients,
        },
      });
      setNewsletterMsg(
        `Newsletter versendet: ${result.sent_count} von ${result.requested_count} Empfängern.`
        + (Array.isArray(result.skipped_emails) && result.skipped_emails.length > 0
          ? ` Übersprungen: ${result.skipped_emails.join(', ')}`
          : '')
      );
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setNewsletterErr(err?.message || 'Newsletter konnte nicht versendet werden');
    } finally {
      setNewsletterSending(false);
    }
  }

  async function ladeWatchlist(key = watchlistKey) {
    setWatchlistErr('');
    setWatchlistMsg('');
    try {
      const data = await apiRequest(`/admin/settings/watchlists/${key}`);
      setWatchlistKey(data.key);
      setWatchlistFile(data.filename);
      setWatchlistContent(data.content || '');
    } catch (err) {
      handleError(err);
      setWatchlistErr(err?.message || 'Watchlist konnte nicht geladen werden');
    }
  }

  async function uebernehmeScreenerWatchlistAusBacktest() {
    setWatchlistErr('');
    setWatchlistMsg('');
    try {
      const data = await apiRequest(`/admin/settings/watchlists/${watchlistKey}/from-backtest`);
      setWatchlistFile(data.filename);
      setWatchlistContent(data.content || '');
      const entries = (data.content || '').split('\n').filter(line => line.trim().startsWith("{'ticker':")).length;
      setWatchlistMsg(`Aus Backtest-null-Zeilen vorbereitet (${entries} Einträge). Bitte prüfen und speichern.`);
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setWatchlistErr(err?.message || 'Backtest-Watchlist konnte nicht übernommen werden');
    }
  }

  function filterBacktestNullRows(content) {
    return String(content || '')
      .split('\n')
      .filter(line => {
        if (!line.trim()) return false;
        const parts = line.split('|').map(part => part.trim());
        return parts.length === 6 && parts[5].toLowerCase() === 'null';
      })
      .join('\n');
  }

  function mergeBacktestNullRows(fullContent, visibleContent) {
    const originalLines = String(fullContent || '').split('\n');
    const editedLines = String(visibleContent || '').split('\n').filter(line => line.trim());
    const nullLineIndexes = originalLines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => {
        const parts = line.split('|').map(part => part.trim());
        return parts.length === 6 && parts[5].toLowerCase() === 'null';
      })
      .map(({ index }) => index);

    if (editedLines.length !== nullLineIndexes.length) {
      throw new Error(`Der Null-Filter zeigt ${nullLineIndexes.length} Zeilen. Bitte keine Zeilen hinzufügen oder entfernen, solange der Filter aktiv ist.`);
    }

    const merged = [...originalLines];
    nullLineIndexes.forEach((lineIndex, position) => {
      merged[lineIndex] = editedLines[position];
    });
    return merged.join('\n');
  }

  async function ladeBacktestWatchlist(key = backtestWatchlistKey) {
    setBacktestWatchlistErr('');
    setBacktestWatchlistMsg('');
    try {
      const data = await apiRequest(`/admin/settings/backtest-watchlists/${key}`);
      const content = data.content || '';
      setBacktestWatchlistKey(data.key);
      setBacktestWatchlistFile(data.filename);
      setBacktestWatchlistContent(content);
      setBacktestWatchlistVisibleContent(backtestWatchlistNullOnly ? filterBacktestNullRows(content) : content);
    } catch (err) {
      handleError(err);
      setBacktestWatchlistErr(err?.message || 'Backtest-Watchlist konnte nicht geladen werden');
    }
  }

  async function ladeRslBacktestMode() {
    setRslBacktestModeErr('');
    setRslBacktestModeMsg('');
    try {
      const data = await apiRequest('/admin/settings/rsl-backtest-mode');
      const mode = data.execution_mode || 'next_daily_open';
      setRslBacktestMode([
        'next_daily_open',
        'historical_strategy_comparison_v09',
        'historical_strategy_comparison_v10',
        'historical_admin_signal_v11',
        'parameter_comparison',
      ].includes(mode) ? mode : 'next_daily_open');
    } catch (err) {
      handleError(err);
      setRslBacktestModeErr(err?.message || 'Backtest-Modus konnte nicht geladen werden');
    }
  }

  async function ladeRslSignalSettings() {
    setRslSignalSettingsErr('');
    setRslSignalSettingsMsg('');
    try {
      const data = await apiRequest('/admin/settings/rsl-signal-settings');
      setRslSignalSettings({ ...DEFAULT_RSL_SIGNAL_SETTINGS, ...(data.markets || {}) });
    } catch (err) {
      handleError(err);
      setRslSignalSettingsErr(err?.message || 'RSL-Signalparameter konnten nicht geladen werden');
    }
  }

  function updateRslSignalSetting(market, key, value) {
    setRslSignalSettings(prev => ({
      ...prev,
      [market]: {
        ...(prev[market] || DEFAULT_RSL_SIGNAL_SETTINGS[market]),
        [key]: value,
      },
    }));
  }

  async function speichernRslSignalSettings() {
    setRslSignalSettingsErr('');
    setRslSignalSettingsMsg('');
    try {
      const data = await apiRequest('/admin/settings/rsl-signal-settings', {
        method: 'PATCH',
        body: { markets: rslSignalSettings },
      });
      setRslSignalSettings({ ...DEFAULT_RSL_SIGNAL_SETTINGS, ...(data.markets || {}) });
      setRslSignalSettingsMsg('RSL-Signalparameter gespeichert');
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setRslSignalSettingsErr(err?.message || 'RSL-Signalparameter konnten nicht gespeichert werden');
    }
  }

  async function uploadConversionRates() {
    setConversionRateErr('');
    setConversionRateMsg('');
    if (!conversionRateFile) {
      setConversionRateErr('Bitte eine ConversionRates-Datei auswählen');
      return;
    }

    const formData = new FormData();
    formData.append('file', conversionRateFile);

    try {
      const data = await apiRequest('/admin/settings/conversion-rates', {
        method: 'POST',
        body: formData,
      });
      setConversionRateFiles(Array.isArray(data) ? data : []);
      setConversionRateMsg(`${conversionRateFile.name} wurde hochgeladen`);
      setConversionRateFile(null);
      if (onIbTaxYearsChanged) {
        await onIbTaxYearsChanged();
      }
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setConversionRateErr(err?.message || 'ConversionRates-Datei konnte nicht hochgeladen werden');
    }
  }

  async function speichernWatchlist() {
    setWatchlistErr('');
    setWatchlistMsg('');
    try {
      const res = await apiRequest(`/admin/settings/watchlists/${watchlistKey}`, {
        method: 'PATCH',
        body: { content: watchlistContent },
        timeoutMs: 120000,
      });
      setWatchlistMsg(res.message || 'Watchlist gespeichert');
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setWatchlistErr(err?.message || 'Watchlist konnte nicht gespeichert werden');
    }
  }

  async function speichernBacktestWatchlist() {
    setBacktestWatchlistErr('');
    setBacktestWatchlistMsg('');
    try {
      const contentToSave = backtestWatchlistNullOnly
        ? mergeBacktestNullRows(backtestWatchlistContent, backtestWatchlistVisibleContent)
        : backtestWatchlistVisibleContent;
      const res = await apiRequest(`/admin/settings/backtest-watchlists/${backtestWatchlistKey}`, {
        method: 'PATCH',
        body: { content: contentToSave },
        timeoutMs: 120000,
      });
      setBacktestWatchlistContent(contentToSave);
      setBacktestWatchlistVisibleContent(backtestWatchlistNullOnly ? filterBacktestNullRows(contentToSave) : contentToSave);
      setBacktestWatchlistMsg(res.message || 'Backtest-Watchlist gespeichert');
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setBacktestWatchlistErr(err?.message || 'Backtest-Watchlist konnte nicht gespeichert werden');
    }
  }

  async function speichernRslBacktestMode() {
    setRslBacktestModeErr('');
    setRslBacktestModeMsg('');
    try {
      const data = await apiRequest('/admin/settings/rsl-backtest-mode', {
        method: 'PATCH',
        body: { execution_mode: rslBacktestMode },
      });
      setRslBacktestMode(data.execution_mode || rslBacktestMode);
      setRslBacktestModeMsg('Backtest-Modus gespeichert');
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setRslBacktestModeErr(err?.message || 'Backtest-Modus konnte nicht gespeichert werden');
    }
  }

  async function pruefeWatchlistSyntax() {
    setWatchlistErr('');
    setWatchlistMsg('');
    try {
      const res = await apiRequest(`/admin/settings/watchlists/${watchlistKey}/validate`, {
        method: 'POST',
        body: { content: watchlistContent },
      });
      setWatchlistMsg(`${res.message} (${res.entries} Einträge)`);
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setWatchlistErr(err?.message || 'Syntaxprüfung fehlgeschlagen');
    }
  }

  async function pruefeBacktestWatchlistSyntax() {
    setBacktestWatchlistErr('');
    setBacktestWatchlistMsg('');
    try {
      const contentToCheck = backtestWatchlistNullOnly
        ? mergeBacktestNullRows(backtestWatchlistContent, backtestWatchlistVisibleContent)
        : backtestWatchlistVisibleContent;
      const res = await apiRequest(`/admin/settings/backtest-watchlists/${backtestWatchlistKey}/validate`, {
        method: 'POST',
        body: { content: contentToCheck },
      });
      setBacktestWatchlistMsg(`${res.message} (${res.entries} Einträge)`);
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setBacktestWatchlistErr(err?.message || 'Syntaxprüfung fehlgeschlagen');
    }
  }

  function toggleBacktestNullOnly() {
    setBacktestWatchlistErr('');
    setBacktestWatchlistMsg('');
    setBacktestWatchlistNullOnly(current => {
      const next = !current;
      setBacktestWatchlistVisibleContent(next ? filterBacktestNullRows(backtestWatchlistContent) : backtestWatchlistContent);
      return next;
    });
  }

  async function speichern() {
    const url    = editUser ? `/admin/users/${editUser.id}` : '/admin/users';
    const method = editUser ? 'PATCH' : 'POST';
    const body   = { ...form };
    body.admin_notifications_enabled = !!body.is_admin && body.admin_notifications_enabled !== false;
    if (editUser && !body.password) delete body.password;
    if (!body.access_until) body.access_until = null;
    if (!body.trial_until) body.trial_until = null;
    if (!body.subscription_until) body.subscription_until = null;

    try {
      await apiRequest(url, { method, body });
      setShowForm(false); setEditUser(null);
      setForm({
        email: '',
        password: '',
        is_admin: false,
        admin_notifications_enabled: true,
        trade_journal_order_form_enabled: false,
        access_until: '',
        plan: 'free',
        plan_status: 'active',
        trial_until: '',
        subscription_until: '',
      });
      ladeUsers();
      ladeUserStats();
      ladeReservedEmails();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      onToast?.(err.message || 'Speichern fehlgeschlagen', 'error');
    }
  }

  async function loeschen(id) {
    if (!(await onConfirm?.('User wirklich löschen?'))) return;
    try {
      await apiRequest(`/admin/users/${id}`, { method: 'DELETE' });
      ladeUsers();
      ladeUserStats();
      ladeReservedEmails();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
    }
  }

  async function reaktiviereReservedEmail(email) {
    setReservedEmailErr('');
    setReservedEmailMsg('');
    if (!(await onConfirm?.(`E-Mail ${email} wieder für Registrierung freigeben? Neue Registrierung erhält dann keinen Demo-Zugang.`))) return;
    try {
      const data = await apiRequest('/admin/reserved-emails/reactivate', {
        method: 'POST',
        body: { email },
      });
      setReservedEmails(Array.isArray(data) ? data : []);
      setReservedEmailMsg(`E-Mail ${email} wurde reaktiviert. Neue Registrierung startet nur mit Start und Konto.`);
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setReservedEmailErr(err?.message || 'Reservierte E-Mail konnte nicht reaktiviert werden');
    }
  }

  async function loescheReservedEmailEndgueltig(email) {
    setReservedEmailErr('');
    setReservedEmailMsg('');
    if (!(await onConfirm?.(`Reservierte E-Mail ${email} endgültig löschen? Danach ist wieder eine normale Registrierung mit Demo möglich.`))) return;
    try {
      const data = await apiRequest(`/admin/reserved-emails/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      setReservedEmails(Array.isArray(data) ? data : []);
      setReservedEmailMsg(`Reservierte E-Mail ${email} wurde endgültig gelöscht. Normale Registrierung ist wieder möglich.`);
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
      setReservedEmailErr(err?.message || 'Reservierte E-Mail konnte nicht gelöscht werden');
    }
  }

  async function toggleActive(id, current) {
    try {
      await apiRequest(`/admin/users/${id}`, {
        method: 'PATCH',
        body: { is_active: !current },
      });
      ladeUsers();
      ladeUserStats();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
    }
  }

  async function markUserVerified(user) {
    if (!user || user.is_verified) return;
    if (!(await onConfirm?.(`User ${user.email} manuell als verifiziert markieren?`))) return;
    try {
      await apiRequest(`/admin/users/${user.id}`, {
        method: 'PATCH',
        body: { is_verified: true },
      });
      setEditUser(current => current && current.id === user.id
        ? { ...current, is_verified: true, verification_expires_at: null }
        : current);
      ladeUsers();
      ladeUserStats();
      ladeAuditLogs();
      onToast?.('User wurde als verifiziert markiert.', 'success');
    } catch (err) {
      handleError(err);
      onToast?.(err.message || 'Verifizierung konnte nicht gesetzt werden', 'error');
    }
  }

  async function toggleModule(userId, modul, currentEffective) {
    try {
      await apiRequest(`/admin/users/${userId}/modules`, {
        method: 'PATCH',
        body: { modul, enabled: !currentEffective },
      });
      ladeUsers();
      ladeAuditLogs();
    } catch (err) {
      handleError(err);
    }
  }

  function openEdit(u) {
    setEditUser(u);
    setForm({
      email: u.email, password: '',
      is_admin: u.is_admin,
      admin_notifications_enabled: u.admin_notifications_enabled !== false,
      trade_journal_order_form_enabled: !!u.trade_journal_order_form_enabled,
      access_until: u.access_until ? u.access_until.split('T')[0] : '',
      plan: u.plan || 'free',
      plan_status: u.plan_status || 'active',
      trial_until: u.trial_until ? u.trial_until.split('T')[0] : '',
      subscription_until: u.subscription_until ? u.subscription_until.split('T')[0] : '',
    });
    setShowForm(true);
  }

  function userHasModuleAccess(user, modul) {
    if (user.is_admin) return true;
    return !!user.modules?.[modul];
  }

  // eslint-disable-next-line no-unused-vars
  function updateChangelogEntry(index, field, value) {
    setChangelogEntries(entries => entries.map((entry, idx) => (
      idx === index ? { ...entry, [field]: value } : entry
    )));
  }

  // eslint-disable-next-line no-unused-vars
  function addChangelogEntry() {
    setChangelogEntries(entries => [...entries, { entry_date: '', change: '' }]);
  }

  // eslint-disable-next-line no-unused-vars
  function removeChangelogEntry(index) {
    setChangelogEntries(entries => {
      const next = entries.filter((_, idx) => idx !== index);
      return next.length > 0 ? next : [{ entry_date: '', change: '' }];
    });
  }

  return (
    <div>
      {indexCheckReminder.should_show && (
        <div
          style={{
            marginBottom: '14px',
            border: '1px solid rgba(245, 158, 11, 0.45)',
            background: 'rgba(245, 158, 11, 0.12)',
            borderRadius: '12px',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
            {indexCheckReminder.message}
          </div>
          <Btn onClick={bestaetigeIndexCheckReminder} color="ghost" size="sm" disabled={indexCheckReminderSaving}>
            {indexCheckReminderSaving ? 'Bestätigt...' : 'Bestätigen'}
          </Btn>
        </div>
      )}
      {indexCheckReminderErr && (
        <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {indexCheckReminderErr}</div>
      )}

      <Card style={{ marginBottom: '24px', padding: '12px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {[
            ['strategies', '📈 Strategien'],
            ['users', '👥 User Verwaltung'],
            ['live_agents', '🟢 Live Agents'],
            ['live_agent_setup', '🤖 Live Agents Setup'],
            ['settings', '⚙ App Einstellungen'],
            ['data_providers', '📡 Datenanbieter'],
            ['billing', '💳 Billing'],
            ['system', '🩺 App System'],
            ['product', '🧩 Produkt-Pipeline'],
            ['content', '📣 Content Studio'],
            ['handbook', '📘 Benutzerhandbuch'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setAdminSection(key)}
              style={{
                background: adminSection === key ? '#0ea5a622' : '#ffffff08',
                color: adminSection === key ? COLORS.cyan : COLORS.text,
                border: `1px solid ${adminSection === key ? COLORS.accent + '55' : COLORS.border}`,
                borderRadius: '12px',
                padding: '10px 14px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      {adminSection === 'users' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <Btn onClick={() => { setShowForm(!showForm); setEditUser(null); setForm({ email: '', password: '', is_admin: false, admin_notifications_enabled: true, trade_journal_order_form_enabled: false, access_until: '', plan: 'free', plan_status: 'active', trial_until: '', subscription_until: '' }); }}>
              {showForm ? '✕ Abbrechen' : '+ Neuer User'}
            </Btn>
          </div>

      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>📊 User-Statistik</h3>
          <Btn onClick={ladeUserStats} color="ghost" size="sm">Aktualisieren</Btn>
        </div>
        {userStatsErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {userStatsErr}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
          {[
            ['Gesamt', userStats.total_users],
            ['Aktiv', userStats.active_users],
            ['Verifiziert', userStats.verified_users],
            ['Admins', userStats.admins],
            ['30-Tage-Test', userStats.free_users],
            ['Essential', userStats.basic_users],
            ['Plus', userStats.premium_users],
            ['Trial', userStats.trial_users],
            ['Login 24h', userStats.recent_logins_24h],
            ['Login 7d', userStats.recent_logins_7d],
          ].map(([label, value]) => (
            <div
              key={label}
              style={{
                padding: '12px',
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                background: '#ffffff05',
              }}
            >
              <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '6px' }}>{label}</div>
              <div style={{ fontSize: '20px', fontWeight: '800', color: COLORS.text }}>{value}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* FORMULAR */}
      {showForm && (
        <Card style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '16px' }}>
            {editUser ? `✏️ User bearbeiten: ${editUser.email}` : '➕ Neuer User'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            <div>
              <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>E-Mail *</label>
              <input style={inputStyle} type="email" autoComplete="off" placeholder="user@example.com"
                value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
                Passwort {editUser ? '(leer = unverändert)' : '*'}
              </label>
              <input style={inputStyle} type="password" autoComplete="new-password"
                placeholder={editUser ? 'Neues Passwort (optional)' : 'Passwort'}
                value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Plan</label>
              <select
                style={{ ...inputStyle, appearance: 'none' }}
                value={form.plan}
                onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}
              >
                {PLAN_LIST.map(plan => (
                  <option key={plan} value={plan}>{formatPlanLabel(plan)}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Plan-Status</label>
              <select
                style={{ ...inputStyle, appearance: 'none' }}
                value={form.plan_status}
                onChange={e => setForm(f => ({ ...f, plan_status: e.target.value }))}
              >
                {PLAN_STATUS_LIST.map(status => (
                  <option key={status} value={status}>{formatPlanStatusLabel(status)}</option>
                ))}
              </select>
            </div>
            {form.plan_status === 'trial' && (
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Trial bis</label>
                <input
                  style={inputStyle}
                  type="date"
                  autoComplete="off"
                  value={form.trial_until}
                  onChange={e => setForm(f => ({ ...f, trial_until: e.target.value }))}
                />
                <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '6px' }}>
                  Wird bei neuer Registrierung normalerweise automatisch auf 30 Tage gesetzt.
                </div>
              </div>
            )}
            {(form.plan === 'basic' || form.plan === 'premium') && (
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Abo bis</label>
                <input
                  style={inputStyle}
                  type="date"
                  autoComplete="off"
                  value={form.subscription_until}
                  onChange={e => setForm(f => ({ ...f, subscription_until: e.target.value }))}
                />
                <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '6px' }}>
                  Relevantes Laufzeitdatum für Essential- und Plus-Zugang.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingTop: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_admin}
                  onChange={e => setForm(f => ({ ...f, is_admin: e.target.checked, admin_notifications_enabled: e.target.checked ? f.admin_notifications_enabled : false }))}
                  style={{ width: '16px', height: '16px', accentColor: COLORS.accent }} />
                <span style={{ fontSize: '14px' }}>Admin-Rechte</span>
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingTop: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: form.is_admin ? 'pointer' : 'default', opacity: form.is_admin ? 1 : 0.45 }}>
                <input
                  type="checkbox"
                  checked={!!form.is_admin && form.admin_notifications_enabled !== false}
                  disabled={!form.is_admin}
                  onChange={e => setForm(f => ({ ...f, admin_notifications_enabled: e.target.checked }))}
                  style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
                />
                <span style={{ fontSize: '14px' }}>Admin-Mails erhalten</span>
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingTop: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.trade_journal_order_form_enabled}
                  onChange={e => setForm(f => ({ ...f, trade_journal_order_form_enabled: e.target.checked }))}
                  style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
                />
                <span style={{ fontSize: '14px' }}>Order-Maske Automation freigeben</span>
              </label>
            </div>
          </div>
          <details style={{ marginTop: '18px' }}>
            <summary style={{ cursor: 'pointer', color: COLORS.muted, fontSize: '13px', fontWeight: '700' }}>
              Erweiterte Felder
            </summary>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', marginTop: '14px' }}>
              {editUser && (
                <div>
                  <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
                    E-Mail-Verifizierung
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <Badge color={editUser.is_verified ? 'green' : 'red'}>
                      {editUser.is_verified ? 'Verifiziert' : 'Nicht verifiziert'}
                    </Badge>
                    {!editUser.is_verified && (
                      <Btn onClick={() => markUserVerified(editUser)} color="green" size="sm">
                        Als verifiziert markieren
                      </Btn>
                    )}
                  </div>
                  {!editUser.is_verified && editUser.verification_expires_at && (
                    <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '6px' }}>
                      Aktueller Code gültig bis {new Date(editUser.verification_expires_at).toLocaleString('de-DE')}.
                    </div>
                  )}
                  {!editUser.is_verified && !editUser.verification_expires_at && (
                    <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '6px' }}>
                      Kein gültiger Verifizierungscode hinterlegt.
                    </div>
                  )}
                </div>
              )}
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
                  Zugang bis (leer = unbegrenzt)
                </label>
                <input
                  style={inputStyle}
                  type="date"
                  autoComplete="off"
                  value={form.access_until}
                  onChange={e => setForm(f => ({ ...f, access_until: e.target.value }))}
                />
                <div style={{ fontSize: '12px', color: COLORS.muted, marginTop: '6px' }}>
                  Nur für manuelle Gesamtsperre oder zeitlich befristeten Sonderzugang.
                </div>
              </div>
            </div>
          </details>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <Btn onClick={speichern} color="green">{editUser ? '💾 Speichern' : '✓ Erstellen'}</Btn>
            <Btn onClick={() => { setShowForm(false); setEditUser(null); }} color="ghost">Abbrechen</Btn>
          </div>
          {editUser && (
              <div style={{ marginTop: '20px', borderTop: `1px solid ${COLORS.border}`, paddingTop: '18px' }}>
                <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Module, Depot, Journal und Automation
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                {MODULE_LIST.map(m => (
                  <button
                    key={m}
                    onClick={() => !editUser.is_admin && toggleModule(editUser.id, m, userHasModuleAccess(editUser, m))}
                    style={{
                      background: userHasModuleAccess(editUser, m) ? '#14532d44' : '#ffffff08',
                      color: userHasModuleAccess(editUser, m) ? COLORS.green : COLORS.muted,
                      border: `1px solid ${userHasModuleAccess(editUser, m) ? COLORS.green + '44' : COLORS.border}`,
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: editUser.is_admin ? 'default' : 'pointer',
                      minWidth: '110px',
                    }}
                  >
                    {(moduleLabelMap[m] || m)}: {editUser.is_admin ? 'An' : userHasModuleAccess(editUser, m) ? 'An' : 'Aus'}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '8px' }}>
                Aktives Depot
              </div>
              {Array.isArray(editUser.depot_positions) && editUser.depot_positions.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {editUser.depot_positions.map(pos => (
                    <Badge key={`${editUser.id}-${pos.index}-${pos.symbol}`} color={pos.index === 'HDAX' ? 'yellow' : 'indigo'}>
                      {pos.symbol} ({pos.index})
                    </Badge>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: COLORS.muted }}>Keine aktiven Depot-Positionen</div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* USER-TABELLE */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', color: COLORS.muted }}>
              Sichtbar: {visibleRangeStart}-{visibleRangeEnd} von {userTotal} Usern
            </div>
            <input
              autoComplete="off"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="User per E-Mail suchen..."
              title="User per E-Mail suchen"
              style={{
                ...inputStyle,
                minWidth: '260px',
                padding: '10px 12px',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Btn
              onClick={exportUsersCsv}
              color="green"
              size="sm"
              disabled={userExporting}
            >
              {userExporting ? 'Exportiere…' : 'CSV Export'}
            </Btn>
            {[
              ['all', 'Alle'],
              ['trial', 'Trial'],
              ['paying', 'Zahlend'],
              ['attention', 'Billing offen'],
              ['none', 'Ohne Billing'],
            ].map(([key, label]) => (
              <Btn
                key={key}
                onClick={() => setUserBillingFilter(key)}
                color={userBillingFilter === key ? 'cyan' : 'ghost'}
                size="sm"
              >
                {label}
              </Btn>
            ))}
          </div>
        </div>
        {userExportErr && (
          <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {userExportErr}</div>
        )}
        <div style={{ overflowX: 'auto', maxHeight: '430px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <SortHeader label="E-Mail" sortKey="email" activeSort={userSort} onToggle={toggleUserSort} />
                <SortHeader label="Rolle" sortKey="role" activeSort={userSort} onToggle={toggleUserSort} />
                <SortHeader label="Mitgliedschaft" sortKey="plan" activeSort={userSort} onToggle={toggleUserSort} />
                <SortHeader label="Account" sortKey="status" activeSort={userSort} onToggle={toggleUserSort} />
                <SortHeader label="Letzter Login" sortKey="last_login_at" activeSort={userSort} onToggle={toggleUserSort} />
                <th style={th}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ ...td, color: COLORS.muted, textAlign: 'center' }}>
                    Keine User für die aktuelle Suche oder den gewählten Billing-Filter
                  </td>
                </tr>
              )}
              {visibleUsers.map(u => (
                <tr key={u.id}>
                  <td style={{ ...td, fontWeight: '600' }}>{u.email}</td>
                  <td style={td}>
                    <div style={{ display: 'grid', gap: '6px', justifyItems: 'start' }}>
                      <Badge color={u.is_admin ? 'yellow' : 'ghost'}>
                        {u.is_admin ? '👑 Admin' : 'User'}
                      </Badge>
                      {u.is_admin && (
                        <Badge color={u.admin_notifications_enabled === false ? 'ghost' : 'green'}>
                          {u.admin_notifications_enabled === false ? 'Admin-Mails aus' : 'Admin-Mails an'}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'grid', gap: '6px', justifyItems: 'start' }}>
                      <Badge color={getPlanBadgeColor(u.effective_plan || u.plan)}>
                        {formatPlanLabel(u.effective_plan || u.plan)}
                      </Badge>
                      <Badge color={u.effective_plan_status === 'expired' ? 'red' : u.effective_plan_status === 'trial' ? 'cyan' : 'ghost'}>
                        {formatPlanStatusLabel(u.effective_plan_status || u.plan_status)}
                      </Badge>
                      <Badge color={getBillingStatusBadgeColor(u.billing_status)}>
                        Billing: {formatBillingStatusLabel(u.billing_status)}
                      </Badge>
                      {(u.billing_current_period_end || u.billing_plan) && (
                        <span style={{ color: COLORS.muted, fontSize: '11px' }}>
                          {u.billing_plan ? formatPlanLabel(u.billing_plan) : 'Stripe'}
                          {u.billing_current_period_end ? ` bis ${new Date(u.billing_current_period_end).toLocaleDateString('de-DE')}` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'grid', gap: '6px', justifyItems: 'start' }}>
                      <button onClick={() => toggleActive(u.id, u.is_active)} style={{
                        background:   u.is_active ? '#14532d44' : '#7f1d1d44',
                        color:        u.is_active ? COLORS.green : COLORS.red,
                        border:       `1px solid ${u.is_active ? COLORS.green + '44' : COLORS.red + '44'}`,
                        borderRadius: '6px', padding: '3px 10px',
                        fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                      }}>
                        {u.is_active ? '✓ Aktiv' : '✗ Gesperrt'}
                      </button>
                      {u.trial_until && (
                        <span style={{ color: COLORS.muted, fontSize: '11px' }}>
                          Trial bis {new Date(u.trial_until).toLocaleDateString('de-DE')}
                        </span>
                      )}
                      {!u.trial_until && u.subscription_until && (
                        <span style={{ color: COLORS.muted, fontSize: '11px' }}>
                          Abo bis {new Date(u.subscription_until).toLocaleDateString('de-DE')}
                        </span>
                      )}
                      {!u.trial_until && !u.subscription_until && (
                        <span style={{ color: COLORS.muted, fontSize: '11px' }}>
                          {u.access_until
                            ? `Zugang bis ${new Date(u.access_until).toLocaleDateString('de-DE')}`
                            : '∞ Unbegrenzt'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ ...td, color: COLORS.muted, fontSize: '13px' }}>
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString('de-DE')
                      : '—'}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <AccountDataExport COLORS={COLORS} userId={u.id} />
                      <Btn
                        onClick={() => toggleUserBillingHistory(u)}
                        color="ghost"
                        size="sm"
                        title="Zahlungsverlauf dieses Users anzeigen"
                      >
                        💳
                      </Btn>
                      <Btn
                        onClick={() => u.billing_customer_id && syncUserBillingHistory(u)}
                        color="ghost"
                        size="sm"
                        title={u.billing_customer_id ? 'Stripe-Historie für diesen User synchronisieren' : 'Noch kein Stripe-Kunde hinterlegt'}
                      >
                        ↻
                      </Btn>
                      <Btn onClick={() => openEdit(u)} color="ghost" size="sm" title="User bearbeiten">✏️</Btn>
                      <Btn onClick={() => loeschen(u.id)} color="red" size="sm" title="User löschen">🗑️</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '14px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '13px', color: COLORS.muted }}>
            {visibleRangeStart}-{visibleRangeEnd} von {userTotal} Usern
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn
              onClick={() => ladeUsers(Math.max(0, userOffset - userLimit))}
              color="ghost"
              size="sm"
              disabled={!canGoUserBack}
            >
              Zurück
            </Btn>
            <Btn
              onClick={() => ladeUsers(userOffset + userLimit)}
              color="ghost"
              size="sm"
              disabled={!canGoUserForward}
            >
              Weiter
            </Btn>
          </div>
        </div>
      </Card>

      {selectedBillingUser && (
        <Card style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '15px' }}>💳 Zahlungsverlauf: {selectedBillingUser.email}</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Btn
                onClick={() => ladeUserBillingHistory(selectedBillingUser)}
                color="ghost"
                size="sm"
                title="Zahlungsverlauf dieses Users neu laden"
              >
                Neu laden
              </Btn>
              <Btn
                onClick={() => { setSelectedBillingUser(null); setSelectedBillingHistory([]); setSelectedBillingHistoryErr(''); }}
                color="ghost"
                size="sm"
                title="Detailansicht des Zahlungsverlaufs schließen"
              >
                Schließen
              </Btn>
            </div>
          </div>
          {selectedBillingHistoryErr && (
            <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {selectedBillingHistoryErr}</div>
          )}
          {selectedBillingHistoryLoading ? (
            <div style={{ color: COLORS.muted, fontSize: '13px' }}>Lade Billing-Historie…</div>
          ) : selectedBillingHistory.length === 0 ? (
            <div style={{ color: COLORS.muted, fontSize: '13px' }}>Keine Zahlungen vorhanden.</div>
          ) : (
            <div style={{ display: 'grid', gap: '10px', maxHeight: '320px', overflowY: 'auto', paddingRight: '4px' }}>
              {selectedBillingHistory.map(entry => (
                <div
                  key={`admin-billing-${entry.id}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) auto auto',
                    gap: '10px',
                    alignItems: 'center',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    background: '#ffffff05',
                    border: `1px solid ${COLORS.border}`,
                  }}
                >
                  <div>
                    <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '700' }}>
                      {formatBillingProductLabel(entry)}
                    </div>
                    <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }}>
                      {new Date(entry.paid_at || entry.created_at).toLocaleString('de-DE')}
                    </div>
                  </div>
                  <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '700' }}>
                    {formatBillingAmount(entry.amount_total, entry.currency)}
                  </div>
                  <Badge color={getBillingHistoryBadgeColor(entry.status)}>
                    {entry.status === 'paid' ? 'Bezahlt' : entry.status === 'payment_failed' ? 'Fehlgeschlagen' : entry.status}
                  </Badge>
                  {entry.hosted_invoice_url ? (
                    <a
                      href={entry.hosted_invoice_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: COLORS.cyan, fontSize: '12px', fontWeight: '700', textDecoration: 'none' }}
                    >
                      Rechnung
                    </a>
                  ) : (
                    <span style={{ color: COLORS.muted, fontSize: '12px' }}>—</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <AdminNewsletterContacts COLORS={COLORS} th={th} td={td} />
      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>🔒 Reservierte E-Mails</h3>
          <Btn onClick={ladeReservedEmails} color="ghost" size="sm">Aktualisieren</Btn>
        </div>
        <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginBottom: '12px' }}>
          Gelöschte Benutzer-E-Mails bleiben gesperrt. Über Reaktivieren wird die E-Mail wieder für eine neue Registrierung freigegeben, jedoch ohne erneute Demo. Der neue Account sieht dann nur Start und Konto.
        </div>
        {reservedEmailErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {reservedEmailErr}</div>}
        {reservedEmailMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '12px' }}>✓ {reservedEmailMsg}</div>}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <th style={th}>E-Mail</th>
                <th style={th}>Status</th>
                <th style={th}>Grund</th>
                <th style={th}>Erstellt</th>
                <th style={th}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {reservedEmails.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ ...td, color: COLORS.muted, textAlign: 'center' }}>
                    Keine reservierten E-Mails
                  </td>
                </tr>
              )}
              {reservedEmails.map(entry => (
                <tr key={entry.email}>
                  <td style={{ ...td, fontWeight: '600' }}>{entry.email}</td>
                  <td style={td}>
                    <Badge color={entry.is_reactivated ? 'cyan' : 'red'}>
                      {entry.is_reactivated ? 'Reaktiviert' : 'Gesperrt'}
                    </Badge>
                  </td>
                  <td style={{ ...td, color: COLORS.muted }}>{entry.reason || '—'}</td>
                  <td style={{ ...td, color: COLORS.muted }}>
                    {entry.created_at ? new Date(entry.created_at).toLocaleString('de-DE') : '—'}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <Btn
                        onClick={() => reaktiviereReservedEmail(entry.email)}
                        color="ghost"
                        size="sm"
                        style={{ opacity: entry.is_reactivated ? 0.5 : 1, pointerEvents: entry.is_reactivated ? 'none' : 'auto' }}
                      >
                        Reaktivieren
                      </Btn>
                      <Btn
                        onClick={() => loescheReservedEmailEndgueltig(entry.email)}
                        color="red"
                        size="sm"
                      >
                        Endgültig löschen
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

        </>
      )}

      {adminSection === 'system' && (
        <>
      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px' }}>🎥 Meeting-Link erstellen</h3>
            <p style={{ margin: '6px 0 0', color: COLORS.muted, fontSize: '12px', lineHeight: 1.5 }}>
              Erstellt gültige Einfach-Trading-Meet-Links. Öffentliche Fantasiecodes werden auf der Meet-Seite abgewiesen.
            </p>
          </div>
          {meetingCopyMsg && <Badge color={meetingCopyMsg.includes('nicht') ? 'red' : 'green'}>{meetingCopyMsg}</Badge>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 0.8fr) minmax(220px, 1.4fr) minmax(150px, 0.8fr)', gap: '12px', alignItems: 'end' }}>
          <label style={{ display: 'block', fontSize: '12px', color: COLORS.muted }}>
            Typ
            <select
              value={meetingType}
              onChange={e => setMeetingType(e.target.value)}
              style={meetingFieldStyle}
            >
              {MEETING_TYPES.map(entry => (
                <option key={entry.key} value={entry.key}>{entry.label}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'block', fontSize: '12px', color: COLORS.muted }}>
            Thema / Kunde
            <input
              value={meetingTopic}
              onChange={e => setMeetingTopic(e.target.value)}
              placeholder="z. B. Robert Einführung"
              style={meetingFieldStyle}
            />
          </label>
          <label style={{ display: 'block', fontSize: '12px', color: COLORS.muted }}>
            Datum
            <input
              type="date"
              value={meetingDate}
              onChange={e => setMeetingDate(e.target.value)}
              style={meetingFieldStyle}
            />
          </label>
        </div>
        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            readOnly
            value={meetingLink}
            style={{ ...meetingFieldStyle, flex: '1 1 420px', marginTop: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
          />
          <Btn onClick={() => copyMeetingText(meetingLink, 'Meeting-Link')} color="cyan" size="sm">
            Link kopieren
          </Btn>
          <Btn
            onClick={() => copyMeetingText(
              `Hallo,\n\nhier ist dein Einfach-Trading Meeting-Link:\n${meetingLink}\n\nBitte nutze diesen Link zum vereinbarten Termin. Kamera und Mikrofon sind beim Beitritt zunächst ausgeschaltet.`,
              'Einladung'
            )}
            color="ghost"
            size="sm"
          >
            Einladung kopieren
          </Btn>
        </div>
        <div style={{ marginTop: '10px', color: COLORS.muted, fontSize: '12px', lineHeight: 1.6 }}>
          Erlaubte Raumtypen: coaching, support, community, webinar und partner. Gäste warten, bis ein Moderator die Konferenz startet.
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>🩺 Monitoring</h3>
          <Btn onClick={ladeMonitoring} color="ghost" size="sm">Aktualisieren</Btn>
        </div>
        {monitoringErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {monitoringErr}</div>}
        <div style={{
          marginBottom: '14px',
          padding: '12px',
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          background: '#ffffff05',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 800, color: COLORS.text }}>CoreS3 Device-Monitor</div>
              <div style={{ marginTop: '4px', fontSize: '12px', color: COLORS.muted }}>
                Erzeugt einen neuen Zugriffstoken für <code>/api/device-monitor/status</code>.
              </div>
            </div>
            <Btn
              onClick={generateDeviceMonitorToken}
              color="green"
              size="sm"
              disabled={deviceMonitorTokenGenerating}
            >
              {deviceMonitorTokenGenerating ? 'Erzeuge...' : 'Token erzeugen'}
            </Btn>
          </div>
          {deviceMonitorTokenErr && (
            <div style={{ marginTop: '10px', color: COLORS.red, fontSize: '12px' }}>⚠ {deviceMonitorTokenErr}</div>
          )}
          {deviceMonitorTokenMsg && (
            <div style={{ marginTop: '10px', color: COLORS.green, fontSize: '12px' }}>{deviceMonitorTokenMsg}</div>
          )}
          {deviceMonitorToken && (
            <div style={{ marginTop: '10px', display: 'grid', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  readOnly
                  value={deviceMonitorToken}
                  style={{ ...inputStyle, flex: 1, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                />
                <Btn onClick={() => copyDeviceMonitorValue(deviceMonitorToken)} color="ghost" size="sm">Token kopieren</Btn>
              </div>
              {deviceMonitorStatusUrl && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    readOnly
                    value={deviceMonitorStatusUrl}
                    style={{ ...inputStyle, flex: 1, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                  />
                  <Btn onClick={() => copyDeviceMonitorValue(deviceMonitorStatusUrl)} color="ghost" size="sm">URL kopieren</Btn>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
          {monitoring.checks.map(check => (
            <div
              key={check.key}
              style={{
                padding: '12px',
                borderRadius: '12px',
                border: `1px solid ${check.status === 'ok' ? COLORS.green + '33' : check.status === 'warn' ? COLORS.yellow + '33' : COLORS.red + '33'}`,
                background: '#ffffff05',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700' }}>{check.label}</div>
                <Badge color={check.status === 'ok' ? 'green' : check.status === 'warn' ? 'yellow' : 'red'}>
                  {formatMonitoringStatusLabel(check.status)}
                </Badge>
              </div>
              <div style={{ fontSize: '12px', color: COLORS.text, lineHeight: 1.6 }}>
                {check.value || '—'}
              </div>
              {check.details && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: COLORS.muted, lineHeight: 1.6 }}>
                  {check.details}
                </div>
              )}
            </div>
          ))}
        </div>
        {monitoring.generated_at && (
          <div style={{ marginTop: '12px', color: COLORS.muted, fontSize: '12px' }}>
            Stand: {new Date(monitoring.generated_at).toLocaleString('de-DE')}
          </div>
        )}
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>🛡 Security-Events</h3>
          <Btn onClick={ladeSecurityEvents} color="ghost" size="sm">Aktualisieren</Btn>
        </div>
        {securityEventsErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {securityEventsErr}</div>}
        <div style={{ maxHeight: '320px', overflowY: 'auto', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <th style={th}>Zeit</th>
                <th style={th}>Event</th>
                <th style={th}>IP</th>
                <th style={th}>E-Mail</th>
                <th style={th}>Pfad</th>
                <th style={th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {securityEvents.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ ...td, textAlign: 'center', color: COLORS.muted }}>
                    Keine Security-Events
                  </td>
                </tr>
              )}
              {securityEvents.map((event, index) => (
                <tr key={`${event.created_at}-${event.event}-${index}`}>
                  <td style={{ ...td, color: COLORS.muted }}>
                    {event.created_at ? new Date(event.created_at).toLocaleString('de-DE') : '—'}
                  </td>
                  <td style={td}><Badge color="red">{event.event}</Badge></td>
                  <td style={td}>{event.ip || '—'}</td>
                  <td style={td}>{event.email || '—'}</td>
                  <td style={td}>{event.path || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'normal' }}>{event.details || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

        </>
      )}

      {adminSection === 'settings' && (
        <>
      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>🔐 Modul-Rechte pro Plan</h3>
          <Btn onClick={ladePlanModuleMatrix} color="ghost" size="sm">Neu laden</Btn>
        </div>
        <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginBottom: '12px' }}>
          Hier legst du zentral fest, welche Bereiche für 30-Tage-Test, Essential und Plus verfügbar sind.
        </div>
        {planModuleMatrixErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {planModuleMatrixErr}</div>}
        {planModuleMatrixMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '10px' }}>✓ {planModuleMatrixMsg}</div>}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <th style={th}>Modul</th>
                <th style={th}>Trial</th>
                <th style={th}>Essential</th>
                <th style={th}>Plus</th>
              </tr>
            </thead>
            <tbody>
              {planModuleMatrixEntries.map(entry => (
                <tr key={entry.module}>
                  <td style={{ ...td, fontWeight: '600' }}>{entry.label}</td>
                  {[
                    ['trial', entry.trial],
                    ['basic', entry.basic],
                    ['premium', entry.premium],
                  ].map(([field, checked]) => (
                    <td key={field} style={td}>
                      <label style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!checked}
                          onChange={e => updatePlanModuleMatrixEntry(entry.module, field, e.target.checked)}
                          style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
                        />
                      </label>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <Btn onClick={speichernPlanModuleMatrix} color="green" size="sm">Rechte-Matrix speichern</Btn>
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>⚙ Allgemeine Einstellungen (SMTP)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>SMTP Host</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={smtp.smtp_host}
              onChange={e => setSmtp(s => ({ ...s, smtp_host: e.target.value }))}
              placeholder="smtp.example.com"
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>SMTP Port</label>
            <input
              style={inputStyle}
              type="number"
              autoComplete="off"
              value={smtp.smtp_port}
              onChange={e => setSmtp(s => ({ ...s, smtp_port: e.target.value }))}
              placeholder="587"
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>SMTP User</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={smtp.smtp_user}
              onChange={e => setSmtp(s => ({ ...s, smtp_user: e.target.value }))}
              placeholder="user"
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Absender (From)</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={smtp.smtp_from}
              onChange={e => setSmtp(s => ({ ...s, smtp_from: e.target.value }))}
              placeholder="noreply@example.com"
            />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0 16px', alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              SMTP Passwort {smtp.smtp_pass_set ? '(gesetzt)' : '(leer)'}
            </label>
            <input
              style={inputStyle}
              type="password"
              autoComplete="off"
              value={smtp.smtp_pass}
              onChange={e => setSmtp(s => ({ ...s, smtp_pass: e.target.value }))}
              placeholder="nur eintragen, wenn ändern"
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={smtp.smtp_tls}
              onChange={e => setSmtp(s => ({ ...s, smtp_tls: e.target.checked }))}
              style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
            />
            <span style={{ fontSize: '14px' }}>TLS aktiv</span>
          </label>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={smtp.signals_notify_premium}
            onChange={e => setSmtp(s => ({ ...s, signals_notify_premium: e.target.checked }))}
            style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
          />
          <span style={{ fontSize: '14px' }}>Plus-User bei neuen Signalen per E-Mail benachrichtigen</span>
        </label>
        {smtpErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {smtpErr}</div>}
        {smtpMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '10px' }}>✓ {smtpMsg}</div>}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
          <Btn onClick={speichernSmtp} color="green" size="sm">SMTP speichern</Btn>
          <Btn onClick={ladeSmtpSettings} color="ghost" size="sm">Neu laden</Btn>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0 8px', alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Test-Empfänger
            </label>
            <input
              style={inputStyle}
              type="email"
              autoComplete="off"
              value={smtpTestEmail}
              onChange={e => setSmtpTestEmail(e.target.value)}
              placeholder="test@example.com"
            />
          </div>
          <Btn onClick={testSmtp} color="accent" size="sm" style={{ marginBottom: '12px' }}>
            Test-E-Mail senden
          </Btn>
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>📈 Chart-Einstellungen</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={uiSettings.tradingview_charts_enabled}
            onChange={e => setUiSettings(s => ({ ...s, tradingview_charts_enabled: e.target.checked }))}
            style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
          />
          <span style={{ fontSize: '14px' }}>TradingView-Chart unter Depot- und Screener-Zeilen aktivieren</span>
        </label>
        <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginBottom: '12px' }}>
          Bei aktiver Option kann pro Zeile ein TradingView-Chart per Button unterhalb des Wertes eingeblendet werden.
        </div>
        {uiSettingsErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {uiSettingsErr}</div>}
        {uiSettingsMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '10px' }}>✓ {uiSettingsMsg}</div>}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Btn onClick={speichernUiSettings} color="green" size="sm">Chart-Einstellungen speichern</Btn>
          <Btn onClick={ladeUiSettings} color="ghost" size="sm">Neu laden</Btn>
        </div>
      </Card>

        </>
      )}

      {adminSection === 'data_providers' && (
        <>
      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px' }}>📡 Datenanbieter</h3>
            <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '6px' }}>
              Zentrale API-Keys, Provider-Auswahl und Ticker-Mapping fuer RSL-Scanner, RSL-Depot, RSL-Journal, Backtests und Index-Invest.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn onClick={() => { ladeUiSettings(); ladeDataProviderSettings(); ladeDataProviderMappings(); }} color="ghost" size="sm">Neu laden</Btn>
          </div>
        </div>
        {dataProviderErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {dataProviderErr}</div>}
        {dataProviderMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '10px' }}>✓ {dataProviderMsg}</div>}
        {uiSettingsErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {uiSettingsErr}</div>}
        {uiSettingsMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '10px' }}>✓ {uiSettingsMsg}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', alignItems: 'end', marginBottom: '12px' }}>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Marketstack API Key {uiSettings.marketstack_api_key_set ? '(gesetzt)' : '(leer)'}
            </label>
            <input
              style={inputStyle}
              type="password"
              autoComplete="off"
              value={uiSettings.marketstack_api_key}
              onChange={e => setUiSettings(s => ({ ...s, marketstack_api_key: e.target.value, clear_marketstack_api_key: false }))}
              placeholder={uiSettings.marketstack_api_key_set ? 'nur eintragen, wenn ändern' : 'Marketstack API Key'}
            />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: COLORS.muted, fontSize: '12px', marginBottom: '16px' }}>
            <input
              type="checkbox"
              checked={!!uiSettings.clear_marketstack_api_key}
              onChange={e => setUiSettings(s => ({ ...s, clear_marketstack_api_key: e.target.checked, marketstack_api_key: e.target.checked ? '' : s.marketstack_api_key }))}
            />
            Key löschen
          </label>
        </div>
        <Btn onClick={speichernUiSettings} color="green" size="sm">API-Key speichern</Btn>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px' }}>🧭 Provider je RSL-Modul</h3>
            <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '6px' }}>
              Main wird zuerst genutzt. Fallback springt ein, wenn Main veraltet oder unvollständig ist.
            </div>
          </div>
          <Btn onClick={speichernDataProviderSettings} color="green" size="sm">Provider speichern</Btn>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '980px' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <th style={th}>Modul</th>
                <th style={th}>Markt</th>
                <th style={th}>Main</th>
                <th style={th}>Fallback</th>
                <th style={th}>Hinweis</th>
              </tr>
            </thead>
            <tbody>
              {(dataProviderSettings.modules || []).flatMap(module => (
                Object.entries((dataProviderSettings.settings || {})[module.key] || {}).map(([marketKey, row], index) => {
                  const marketLabel = (dataProviderSettings.markets || []).find(market => market.key === marketKey)?.label || marketKey;
                  const selectStyle = { ...inputStyle, width: '150px', marginBottom: 0, padding: '7px 8px', fontSize: '12px' };
                  const primaryProviders = Array.isArray(module.primary_providers)
                    ? (dataProviderSettings.providers || []).filter(provider => module.primary_providers.includes(provider.key))
                    : (dataProviderSettings.providers || []);
                  const fallbackProviders = Array.isArray(module.fallback_providers)
                    ? (dataProviderSettings.providers || []).filter(provider => module.fallback_providers.includes(provider.key))
                    : (dataProviderSettings.providers || []);
                  return (
                    <tr key={`${module.key}-${marketKey}`}>
                      {index === 0 && (
                        <td style={{ ...td, fontWeight: 700 }} rowSpan={Object.keys((dataProviderSettings.settings || {})[module.key] || {}).length}>
                          <div>{module.label || module.key}</div>
                          <div style={{ color: COLORS.muted, fontSize: '11px', fontWeight: 500, marginTop: '4px' }}>{module.description}</div>
                        </td>
                      )}
                      <td style={td}>{marketLabel}</td>
                      <td style={td}>
                        <select
                          value={row.primary_provider || 'yahoo'}
                          onChange={e => updateDataProviderSetting(module.key, marketKey, 'primary_provider', e.target.value)}
                          style={selectStyle}
                        >
                          {primaryProviders.map(provider => (
                            <option key={provider.key} value={provider.key}>{provider.label || DATA_PROVIDER_LABELS[provider.key] || provider.key}</option>
                          ))}
                        </select>
                      </td>
                      <td style={td}>
                        <select
                          value={row.fallback_provider || 'none'}
                          onChange={e => updateDataProviderSetting(module.key, marketKey, 'fallback_provider', e.target.value)}
                          style={selectStyle}
                        >
                          {fallbackProviders.map(provider => (
                            <option key={provider.key} value={provider.key}>{provider.label || DATA_PROVIDER_LABELS[provider.key] || provider.key}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...td, color: COLORS.muted, fontSize: '12px' }}>
                        {row.fallback_provider && row.fallback_provider !== 'none'
                          ? `Fallback ${DATA_PROVIDER_LABELS[row.fallback_provider] || row.fallback_provider} wird genutzt, wenn Main veraltet oder unvollständig ist.`
                          : 'Kein Fallback aktiv.'}
                      </td>
                    </tr>
                  );
                })
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px' }}>🧩 Ticker-Mapping</h3>
            <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '6px' }}>
              App-Symbol auf Anbieter-Symbole mappen, z. B. Klassenaktien, Index-Symbole, deutsche Suffixe oder ETF-Sonderfälle.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn onClick={addDataProviderMapping} color="ghost" size="sm">Mapping hinzufügen</Btn>
            <Btn onClick={speichernDataProviderMappings} color="green" size="sm">Mapping speichern</Btn>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1180px' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <th style={th}>Scope</th>
                <th style={th}>App-Ticker</th>
                <th style={th}>Yahoo</th>
                <th style={th}>Marketstack</th>
                <th style={th}>TradingView</th>
                <th style={th}>Aktiv</th>
                <th style={th}>Notiz</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {dataProviderMappings.map((row, index) => (
                <tr key={`${row.scope}-${row.app_symbol}-${index}`}>
                  {['scope', 'app_symbol', 'yahoo_symbol', 'marketstack_symbol', 'tradingview_symbol'].map(field => (
                    <td key={field} style={td}>
                      <input
                        style={{ ...inputStyle, marginBottom: 0, padding: '7px 8px', fontSize: '12px' }}
                        value={row[field] || ''}
                        onChange={e => updateDataProviderMapping(index, field, e.target.value)}
                      />
                    </td>
                  ))}
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={row.active !== false}
                      onChange={e => updateDataProviderMapping(index, 'active', e.target.checked)}
                    />
                  </td>
                  <td style={td}>
                    <input
                      style={{ ...inputStyle, marginBottom: 0, padding: '7px 8px', fontSize: '12px' }}
                      value={row.note || ''}
                      onChange={e => updateDataProviderMapping(index, 'note', e.target.value)}
                    />
                  </td>
                  <td style={td}>
                    <Btn onClick={() => removeDataProviderMapping(index)} color="red" size="sm">Löschen</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>🧪 Anbieter testen</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(160px, 1fr))', gap: '12px', alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Modul</label>
            <select
              value={dataProviderTest.module_key}
              onChange={e => setDataProviderTest(test => ({ ...test, module_key: e.target.value }))}
              style={inputStyle}
            >
              {(dataProviderSettings.modules || []).map(module => (
                <option key={module.key} value={module.key}>{module.label || module.key}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Markt</label>
            <select
              value={dataProviderTest.market_key}
              onChange={e => setDataProviderTest(test => ({ ...test, market_key: e.target.value }))}
              style={inputStyle}
            >
              {(dataProviderSettings.markets || []).map(market => (
                <option key={market.key} value={market.key}>{market.label || market.key}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Anbieter</label>
            <select
              value={dataProviderTest.provider}
              onChange={e => setDataProviderTest(test => ({ ...test, provider: e.target.value }))}
              style={inputStyle}
            >
              <option value="configured">Konfiguration</option>
              {(dataProviderSettings.providers || []).filter(provider => provider.key !== 'none').map(provider => (
                <option key={provider.key} value={provider.key}>{provider.label || DATA_PROVIDER_LABELS[provider.key] || provider.key}</option>
              ))}
            </select>
          </div>
          <Btn onClick={testeDataProvider} color="accent" size="sm" disabled={dataProviderTesting}>
            {dataProviderTesting ? 'Teste...' : 'Test starten'}
          </Btn>
        </div>
        {dataProviderTestResult && (
          <div style={{ marginTop: '14px', padding: '12px', borderRadius: '10px', border: `1px solid ${COLORS.border}`, background: '#ffffff06', fontSize: '13px', lineHeight: 1.7 }}>
            <strong>{DATA_PROVIDER_LABELS[dataProviderTestResult.provider] || dataProviderTestResult.provider}</strong>
            {' · '}
            {dataProviderTestResult.message}
            <br />
            Datenstand: {dataProviderTestResult.latest_date || '—'} · Gefunden: {dataProviderTestResult.found_count} · Fehlend: {(dataProviderTestResult.missing_symbols || []).join(', ') || '—'}
          </div>
        )}
      </Card>

        </>
      )}

      {adminSection === 'billing' && (
        <>
      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px' }}>🧾 Belege Bulk-Download</h3>
            <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginTop: '8px' }}>
              Lädt alle lokal bekannten Stripe-PDF-Belege für den gewählten Monat als ZIP.
            </div>
          </div>
          <Btn onClick={importiereBillingHistorie} color="ghost" size="sm" title="Bestehende Stripe-Rechnungen für alle User importieren">
            {billingImporting ? 'Import läuft...' : 'Stripe-Historie importieren'}
          </Btn>
        </div>
        {(() => {
          const billingExportFieldStyle = {
            ...inputStyle,
            width: '100%',
            height: '38px',
            minHeight: '38px',
            boxSizing: 'border-box',
            marginBottom: 0,
          };
          return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))',
            gap: '12px',
            alignItems: 'end',
          }}
        >
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Jahr</label>
            <input
              style={billingExportFieldStyle}
              type="number"
              min="2020"
              max="2100"
              value={billingInvoiceExport.year}
              onChange={e => setBillingInvoiceExport(s => ({ ...s, year: Number(e.target.value) || new Date().getFullYear() }))}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Monat</label>
            <select
              style={billingExportFieldStyle}
              value={billingInvoiceExport.month}
              onChange={e => setBillingInvoiceExport(s => ({ ...s, month: Number(e.target.value) || 1 }))}
            >
              {Array.from({ length: 12 }, (_, index) => index + 1).map(month => (
                <option key={month} value={month}>
                  {String(month).padStart(2, '0')}
                </option>
              ))}
            </select>
          </div>
          <Btn
            onClick={ladeBillingBelegeZip}
            color="green"
            size="md"
            title="Alle verfügbaren Rechnungen und Zahlungsbelege für den gewählten Monat als ZIP herunterladen"
            style={{ width: '100%', height: '38px', minHeight: '38px', boxSizing: 'border-box' }}
          >
            {billingInvoiceExportLoading ? 'ZIP wird erstellt...' : 'ZIP herunterladen'}
          </Btn>
        </div>
          );
        })()}
        <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginTop: '10px' }}>
          Falls Belege fehlen, zuerst die Stripe-Historie importieren.
        </div>
        {billingInvoiceExportErr && (
          <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '10px' }}>
            ⚠ {billingInvoiceExportErr}
          </div>
        )}
        {billingInvoiceExportMsg && (
          <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '10px' }}>
            ✓ {billingInvoiceExportMsg}
          </div>
        )}
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>💳 Billing Einstellungen</h3>
          <Btn onClick={ladeBillingSettings} color="ghost" size="sm" title="Billing-Einstellungen neu laden">Neu laden</Btn>
        </div>
        <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginBottom: '12px' }}>
          Hier hinterlegst du die technische Billing-Konfiguration fuer Abos und Anleitung-Einmalzahlungen. Der eigentliche Checkout-Flow kommt danach.
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={billing.billing_enabled}
            onChange={e => setBilling(s => ({ ...s, billing_enabled: e.target.checked }))}
            style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
          />
          <span style={{ fontSize: '14px' }}>Billing global aktivieren</span>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Provider</label>
            <select
              value={billing.billing_provider}
              onChange={e => setBilling(s => ({ ...s, billing_provider: e.target.value }))}
              style={inputStyle}
            >
              <option value="stripe">stripe</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={billing.billing_portal_enabled}
              onChange={e => setBilling(s => ({ ...s, billing_portal_enabled: e.target.checked }))}
              style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
            />
            <span style={{ fontSize: '14px' }}>Customer Portal aktivieren</span>
          </label>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Stripe Publishable Key</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={billing.billing_public_key}
              onChange={e => setBilling(s => ({ ...s, billing_public_key: e.target.value }))}
              placeholder="pk_live_..."
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Stripe Secret Key {billing.billing_secret_key_set ? '(gesetzt)' : '(leer)'}
            </label>
            <input
              style={inputStyle}
              type="password"
              autoComplete="off"
              value={billing.billing_secret_key}
              onChange={e => setBilling(s => ({ ...s, billing_secret_key: e.target.value }))}
              placeholder="nur eintragen, wenn ändern"
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Stripe Webhook Secret {billing.billing_webhook_secret_set ? '(gesetzt)' : '(leer)'}
            </label>
            <input
              style={inputStyle}
              type="password"
              autoComplete="off"
              value={billing.billing_webhook_secret}
              onChange={e => setBilling(s => ({ ...s, billing_webhook_secret: e.target.value }))}
              placeholder="whsec_..."
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Success URL</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={billing.billing_success_url}
              onChange={e => setBilling(s => ({ ...s, billing_success_url: e.target.value }))}
              placeholder="https://app.einfach-trading.de/konto?billing=success"
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Cancel URL</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={billing.billing_cancel_url}
              onChange={e => setBilling(s => ({ ...s, billing_cancel_url: e.target.value }))}
              placeholder="https://app.einfach-trading.de/konto?billing=cancel"
            />
          </div>
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>🏷 Stripe Price IDs</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={billing.basic_monthly_enabled}
                onChange={e => setBilling(s => ({ ...s, basic_monthly_enabled: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
              />
              <span style={{ fontSize: '12px', color: COLORS.muted }}>Essential monatlich aktiv</span>
            </label>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Essential monatlich</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={billing.stripe_price_basic_monthly}
              onChange={e => setBilling(s => ({ ...s, stripe_price_basic_monthly: e.target.value }))}
              placeholder="price_..."
            />
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={billing.basic_yearly_enabled}
                onChange={e => setBilling(s => ({ ...s, basic_yearly_enabled: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
              />
              <span style={{ fontSize: '12px', color: COLORS.muted }}>Essential jährlich aktiv</span>
            </label>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Essential jährlich</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={billing.stripe_price_basic_yearly}
              onChange={e => setBilling(s => ({ ...s, stripe_price_basic_yearly: e.target.value }))}
              placeholder="price_..."
            />
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={billing.premium_monthly_enabled}
                onChange={e => setBilling(s => ({ ...s, premium_monthly_enabled: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
              />
              <span style={{ fontSize: '12px', color: COLORS.muted }}>Plus monatlich aktiv</span>
            </label>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Plus monatlich</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={billing.stripe_price_premium_monthly}
              onChange={e => setBilling(s => ({ ...s, stripe_price_premium_monthly: e.target.value }))}
              placeholder="price_..."
            />
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={billing.premium_yearly_enabled}
                onChange={e => setBilling(s => ({ ...s, premium_yearly_enabled: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
              />
              <span style={{ fontSize: '12px', color: COLORS.muted }}>Plus jährlich aktiv</span>
            </label>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Plus jährlich</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={billing.stripe_price_premium_yearly}
              onChange={e => setBilling(s => ({ ...s, stripe_price_premium_yearly: e.target.value }))}
              placeholder="price_..."
            />
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={billing.coaching_enabled}
                onChange={e => setBilling(s => ({ ...s, coaching_enabled: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
              />
              <span style={{ fontSize: '12px', color: COLORS.muted }}>Anleitung aktiv</span>
            </label>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Anleitung Einmalzahlung</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={billing.stripe_price_coaching}
              onChange={e => setBilling(s => ({ ...s, stripe_price_coaching: e.target.value }))}
              placeholder="price_..."
            />
          </div>
        </div>
        {billingErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '10px' }}>⚠ {billingErr}</div>}
        {billingMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '10px' }}>✓ {billingMsg}</div>}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <Btn onClick={speichernBilling} color="green" size="sm" title="Billing-Konfiguration speichern">Billing speichern</Btn>
          <Btn onClick={importiereBillingHistorie} color="ghost" size="sm" title="Bestehende Stripe-Rechnungen für alle User importieren">
            {billingImporting ? 'Import läuft...' : 'Stripe-Historie importieren'}
          </Btn>
        </div>
      </Card>
        </>
      )}

      {adminSection === 'product' && (
        <>
      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>📋 Feature-Liste</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn onClick={addFeatureEntry} color="ghost" size="sm">+ Feature</Btn>
            <Btn onClick={ladeFeatures} color="ghost" size="sm">Neu laden</Btn>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {featureEntries.map((entry, index) => (
            <div
              key={`feature-${index}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '320px 1fr auto',
                gap: '10px',
                alignItems: 'start',
                padding: '12px',
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                background: '#ffffff05',
              }}
            >
              <div>
                <input
                  style={{ ...inputStyle, marginBottom: '10px' }}
                  autoComplete="off"
                  value={entry.title}
                  onChange={e => updateFeatureEntry(index, 'title', e.target.value)}
                  placeholder="Feature-Titel"
                />
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    ['hoch', 'Hoch'],
                    ['mittel', 'Mittel'],
                    ['niedrig', 'Niedrig'],
                  ].map(([value, label]) => (
                    <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: COLORS.muted }}>
                      <input
                        type="radio"
                        name={`feature-priority-${index}`}
                        checked={(entry.priority || 'mittel') === value}
                        onChange={() => updateFeatureEntry(index, 'priority', value)}
                        style={{ width: '15px', height: '15px', accentColor: COLORS.accent }}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <textarea
                value={entry.description}
                onChange={e => updateFeatureEntry(index, 'description', e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '72px',
                  background: themeMode === 'light' ? '#f7fbfd' : '#0b0d12',
                  color: COLORS.text,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  padding: '12px',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontSize: '14px',
                }}
                placeholder="Feature beschreiben"
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Btn onClick={() => addFeatureToRoadmap(entry)} color="ghost" size="sm">In Roadmap</Btn>
                <Btn onClick={() => removeFeatureEntry(index)} color="red" size="sm">Entfernen</Btn>
              </div>
            </div>
          ))}
        </div>
        {featureErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '10px' }}>⚠ {featureErr}</div>}
        {featureMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '10px' }}>✓ {featureMsg}</div>}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <Btn onClick={speichernFeatures} color="green" size="sm">Feature-Liste speichern</Btn>
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>🛣 Roadmap</h3>
          <Btn onClick={ladeRoadmap} color="ghost" size="sm">Neu laden</Btn>
        </div>
        <div style={{ fontSize: '13px', color: COLORS.muted, marginBottom: '12px' }}>
          Roadmap-Einträge werden aus der Feature-Liste übernommen. Hier pflegst du Status, Ziel und ob ein Punkt im Changelog erscheinen soll.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {[
            ['geplant', 'Geplant'],
            ['in_arbeit', 'In Arbeit'],
            ['erledigt', 'Erledigt'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleRoadmapFilter(key)}
              style={{
                background: roadmapFilters[key] ? COLORS.accent : 'transparent',
                color: roadmapFilters[key] ? '#fff' : COLORS.muted,
                border: `1px solid ${roadmapFilters[key] ? COLORS.accent : COLORS.border}`,
                borderRadius: '999px',
                padding: '7px 12px',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {roadmapEntries
            .map((entry, index) => ({ entry, index }))
            .filter(({ entry }) => roadmapFilters[normalizeRoadmapStatus(entry.status)])
            .map(({ entry, index }) => (
            <div
              key={`roadmap-${index}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 180px 1fr 180px auto',
                gap: '10px',
                alignItems: 'start',
                padding: '12px',
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                background: '#ffffff05',
              }}
            >
              <div>
                <input
                  style={{ ...inputStyle, marginBottom: '10px' }}
                  autoComplete="off"
                  value={entry.title}
                  onChange={e => updateRoadmapEntry(index, 'title', e.target.value)}
                  placeholder="Titel"
                />
                <input
                  style={{ ...inputStyle, marginBottom: 0 }}
                  autoComplete="off"
                  value={entry.target || ''}
                  onChange={e => updateRoadmapEntry(index, 'target', e.target.value)}
                  placeholder="Zieltermin / Zielfenster"
                />
              </div>
              <select
                value={normalizeRoadmapStatus(entry.status)}
                onChange={e => updateRoadmapEntry(index, 'status', e.target.value)}
                style={{ ...inputStyle, marginBottom: 0 }}
              >
                <option value="geplant">geplant</option>
                <option value="in_arbeit">in arbeit</option>
                <option value="erledigt">erledigt</option>
              </select>
              <textarea
                value={entry.description}
                onChange={e => updateRoadmapEntry(index, 'description', e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '72px',
                  background: themeMode === 'light' ? '#f7fbfd' : '#0b0d12',
                  color: COLORS.text,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  padding: '12px',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontSize: '14px',
                }}
                placeholder="Roadmap-Eintrag beschreiben"
              />
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!entry.show_in_changelog}
                    onChange={e => updateRoadmapEntry(index, 'show_in_changelog', e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
                  />
                  <span style={{ fontSize: '13px' }}>Im Changelog anzeigen</span>
                </label>
                <input
                  style={{ ...inputStyle, marginBottom: 0, opacity: entry.show_in_changelog ? 1 : 0.55 }}
                  type="date"
                  autoComplete="off"
                  value={entry.changelog_date || ''}
                  onChange={e => updateRoadmapEntry(index, 'changelog_date', e.target.value)}
                  disabled={!entry.show_in_changelog}
                />
              </div>
              <Btn onClick={() => removeRoadmapEntry(index)} color="red" size="sm">Entfernen</Btn>
            </div>
          ))}
          {roadmapEntries.length === 0 && (
            <div style={{ color: COLORS.muted, fontSize: '13px' }}>
              Noch keine Roadmap-Einträge vorhanden. Übernimm Features per Klick aus der Feature-Liste.
            </div>
          )}
          {roadmapEntries.length > 0 && roadmapEntries.filter(entry => roadmapFilters[normalizeRoadmapStatus(entry.status)]).length === 0 && (
            <div style={{ color: COLORS.muted, fontSize: '13px' }}>
              Für die aktuelle Filterauswahl sind keine Roadmap-Einträge vorhanden.
            </div>
          )}
        </div>
        {roadmapErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '10px' }}>⚠ {roadmapErr}</div>}
        {roadmapMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '10px' }}>✓ {roadmapMsg}</div>}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <Btn onClick={speichernRoadmap} color="green" size="sm">Roadmap speichern</Btn>
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>🗓 Changelog</h3>
          <Btn onClick={ladeChangelog} color="ghost" size="sm">Neu laden</Btn>
        </div>
        <div style={{ fontSize: '13px', color: COLORS.muted, marginBottom: '12px' }}>
          Das Changelog wird aus der Roadmap abgeleitet. Nur ausgewählte Roadmap-Einträge erscheinen später für Nutzer.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {changelogEntries.map((entry, index) => (
            <div
              key={`changelog-${index}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '180px 1fr',
                gap: '10px',
                alignItems: 'start',
                padding: '12px',
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                background: '#ffffff05',
              }}
            >
              <div style={{ ...inputStyle, marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                {entry.entry_date}
              </div>
              <div style={{
                minHeight: '72px',
                background: themeMode === 'light' ? '#f7fbfd' : '#0b0d12',
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '10px',
                padding: '12px',
                boxSizing: 'border-box',
                fontSize: '14px',
                whiteSpace: 'pre-wrap',
              }}>
                {entry.change}
              </div>
            </div>
          ))}
          {changelogEntries.length === 0 && (
            <div style={{ color: COLORS.muted, fontSize: '13px' }}>
              Aktuell keine für das Changelog ausgewählten Roadmap-Einträge vorhanden.
            </div>
          )}
        </div>
        {changelogMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '10px' }}>✓ {changelogMsg}</div>}
        {changelogErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '10px' }}>⚠ {changelogErr}</div>}
      </Card>
        </>
      )}

      {adminSection === 'users' && (
        <>
      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>📣 Newsletter</h3>
          <div style={{ color: COLORS.muted, fontSize: '12px' }}>
            Versand an ausgewählte Benutzer
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0 16px' }}>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Betreff</label>
            <input
              style={inputStyle}
              autoComplete="off"
              value={newsletterSubject}
              onChange={e => setNewsletterSubject(e.target.value)}
              placeholder="Newsletter-Betreff"
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Inhalt</label>
            <textarea
              value={newsletterMessage}
              onChange={e => setNewsletterMessage(e.target.value)}
              style={{
                width: '100%',
                minHeight: '140px',
                background: themeMode === 'light' ? '#f7fbfd' : '#0b0d12',
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '10px',
                padding: '12px',
                boxSizing: 'border-box',
                resize: 'vertical',
                fontSize: '14px',
                marginBottom: '12px',
              }}
              placeholder="Newsletter-Inhalt"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <Btn onClick={selectAllActiveNewsletterRecipients} color="ghost" size="sm">Alle aktiven</Btn>
          <Btn onClick={selectPremiumNewsletterRecipients} color="ghost" size="sm">Nur Plus</Btn>
          <Btn onClick={() => setNewsletterRecipients([])} color="ghost" size="sm">Auswahl leeren</Btn>
        </div>

        <div style={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
          background: themeMode === 'light' ? '#f7fbfd' : '#ffffff05',
          padding: '12px',
          maxHeight: '280px',
          overflowY: 'auto',
          marginBottom: '12px',
        }}>
          <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '10px' }}>
            Empfänger auswählen ({newsletterRecipients.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {users.length === 0 && (
              <div style={{ color: COLORS.muted, fontSize: '13px' }}>Keine Benutzer vorhanden.</div>
            )}
            {users.map(user => {
              const checked = newsletterRecipients.includes(user.email);
              return (
                <label
                  key={user.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '8px 10px',
                    borderRadius: '10px',
                    border: `1px solid ${checked ? COLORS.accent : COLORS.border}`,
                    background: checked ? '#0ea5a614' : (themeMode === 'light' ? '#f7fbfd' : '#ffffff03'),
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleNewsletterRecipient(user.email)}
                      style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
                    />
                    <span style={{ fontSize: '14px', color: COLORS.text }}>{user.email}</span>
                    <Badge color={user.is_admin ? 'yellow' : getPlanBadgeColor(user.effective_plan || user.plan || 'free')}>
                      {user.is_admin ? 'Admin' : formatPlanLabel(user.effective_plan || user.plan || 'free')}
                    </Badge>
                    {user.trade_journal_order_form_enabled === false && <Badge color="ghost">Nur Vorlagen</Badge>}
                    {!user.is_active && <Badge color="red">Inaktiv</Badge>}
                    {user.email_notifications_enabled === false && <Badge color="ghost">Mail aus</Badge>}
                  </div>
                  <div style={{ fontSize: '12px', color: COLORS.muted }}>
                    {formatPlanStatusLabel(user.effective_plan_status || user.plan_status || 'active')}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {newsletterErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {newsletterErr}</div>}
        {newsletterMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '10px' }}>✓ {newsletterMsg}</div>}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Btn onClick={sendNewsletter} color="green" size="sm">
            {newsletterSending ? 'Versende...' : 'Newsletter senden'}
          </Btn>
        </div>
      </Card>

        </>
      )}

      {adminSection === 'strategies' && (
        <>
      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>💱 ConversionRates verwalten</h3>
          <Btn onClick={ladeConversionRates} color="ghost" size="sm">Neu laden</Btn>
        </div>
        <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.7, marginBottom: '14px' }}>
          Lade hier neue <code style={{ color: COLORS.text }}>ConversionRates_YYYY.xml</code>-Dateien hoch. Verfügbare Jahre erscheinen danach automatisch in der IB-Auswertung beim Steuerjahr.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) auto', gap: '10px', alignItems: 'end', marginBottom: '12px' }}>
          <input
            type="file"
            accept=".xml,text/xml,application/xml"
            onChange={e => setConversionRateFile(e.target.files?.[0] || null)}
            style={{ ...inputStyle, marginBottom: 0, padding: '7px 10px' }}
          />
          <Btn onClick={uploadConversionRates} color="green" size="sm">Datei hochladen</Btn>
        </div>
        {conversionRateErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {conversionRateErr}</div>}
        {conversionRateMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '10px' }}>✓ {conversionRateMsg}</div>}
        <div style={{ display: 'grid', gap: '8px' }}>
          {conversionRateFiles.length === 0 ? (
            <div style={{ color: COLORS.muted, fontSize: '13px' }}>Keine ConversionRates-Dateien vorhanden</div>
          ) : (
            conversionRateFiles.map(file => (
              <div
                key={file.year}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  border: `1px solid ${COLORS.border}`,
                  background: themeMode === 'light' ? '#f7fbfd' : '#ffffff06',
                }}
              >
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700 }}>{file.year}</div>
                  <div style={{ fontSize: '12px', color: COLORS.muted }}>{file.filename}</div>
                </div>
                <div style={{ fontSize: '12px', color: COLORS.muted }}>
                  {typeof file.size_bytes === 'number' ? `${Math.round(file.size_bytes / 1024)} KB` : '—'}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>📝 RSL Screener Watchlist bearbeiten</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={watchlistKey}
              onChange={e => ladeWatchlist(e.target.value)}
              style={{
                background: '#ffffff08',
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                padding: '8px 10px',
              }}
            >
              <option value="hdax">HDAX</option>
              <option value="nasdaq100">NASDAQ 100</option>
              <option value="sp500">SP500</option>
              <option value="stoxx600">STOXX 600</option>
            </select>
            <Btn onClick={uebernehmeScreenerWatchlistAusBacktest} color="cyan" size="sm">Aus Backtest null übernehmen</Btn>
            <Btn onClick={() => ladeWatchlist(watchlistKey)} color="ghost" size="sm">Neu laden</Btn>
          </div>
        </div>
        <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '8px' }}>
          Datei: {watchlistFile || '—'}
        </div>
        <textarea
          value={watchlistContent}
          onChange={e => setWatchlistContent(e.target.value)}
          style={{
            width: '100%',
            minHeight: '300px',
            background: themeMode === 'light' ? '#f7fbfd' : '#0b0d12',
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '10px',
            padding: '12px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '12px',
            boxSizing: 'border-box',
            marginBottom: '10px',
          }}
        />
        {watchlistErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {watchlistErr}</div>}
        {watchlistMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '10px' }}>✓ {watchlistMsg}</div>}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Btn onClick={pruefeWatchlistSyntax} color="ghost" size="sm">Syntax prüfen</Btn>
          <Btn onClick={speichernWatchlist} color="green" size="sm">Watchlist speichern</Btn>
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px' }}>🎚️ RSL-Scanner Signal-Parameter</h3>
            <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '6px' }}>
              Steuert Halten, Beobachten und Austauschen pro Index. Deaktivierte Filter werden in der Signalberechnung ignoriert.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn onClick={ladeRslSignalSettings} color="ghost" size="sm">Neu laden</Btn>
            <Btn onClick={speichernRslSignalSettings} color="green" size="sm">Parameter speichern</Btn>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <th style={th}>Index</th>
                <th style={th}>MA-Filter</th>
                <th style={th}>MA kurz/lang</th>
                <th style={th}>K-Ausstieg Top %</th>
                <th style={th}>Index-Vergleich</th>
                <th style={th}>Mindest-RSL</th>
              </tr>
            </thead>
            <tbody>
              {RSL_SIGNAL_MARKETS.map(({ key, label }) => {
                const row = rslSignalSettings[key] || DEFAULT_RSL_SIGNAL_SETTINGS[key];
                const smallInput = {
                  ...inputStyle,
                  width: '74px',
                  marginBottom: 0,
                  padding: '7px 8px',
                  fontSize: '12px',
                };
                return (
                  <tr key={key}>
                    <td style={td}><strong>{label}</strong></td>
                    <td style={td}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: COLORS.text }}>
                        <input
                          type="checkbox"
                          checked={!!row.ma_filter_enabled}
                          onChange={e => updateRslSignalSetting(key, 'ma_filter_enabled', e.target.checked)}
                        />
                        aktiv
                      </label>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="number"
                          min="2"
                          max="52"
                          value={row.ma_short_weeks}
                          disabled={!row.ma_filter_enabled}
                          onChange={e => updateRslSignalSetting(key, 'ma_short_weeks', Number(e.target.value))}
                          style={smallInput}
                        />
                        <span style={{ color: COLORS.muted }}>/</span>
                        <input
                          type="number"
                          min="3"
                          max="104"
                          value={row.ma_long_weeks}
                          disabled={!row.ma_filter_enabled}
                          onChange={e => updateRslSignalSetting(key, 'ma_long_weeks', Number(e.target.value))}
                          style={smallInput}
                        />
                      </div>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!row.k_exit_enabled}
                          onChange={e => updateRslSignalSetting(key, 'k_exit_enabled', e.target.checked)}
                        />
                        <input
                          type="number"
                          min="1"
                          max="100"
                          step="0.1"
                          value={row.k_exit_top_pct}
                          disabled={!row.k_exit_enabled}
                          onChange={e => updateRslSignalSetting(key, 'k_exit_top_pct', Number(e.target.value))}
                          style={smallInput}
                        />
                      </div>
                    </td>
                    <td style={td}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: COLORS.text }}>
                        <input
                          type="checkbox"
                          checked={!!row.index_compare_enabled}
                          onChange={e => updateRslSignalSetting(key, 'index_compare_enabled', e.target.checked)}
                        />
                        aktiv
                      </label>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!row.rsl_min_enabled}
                          onChange={e => updateRslSignalSetting(key, 'rsl_min_enabled', e.target.checked)}
                        />
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.01"
                          value={row.rsl_min_value}
                          disabled={!row.rsl_min_enabled}
                          onChange={e => updateRslSignalSetting(key, 'rsl_min_value', Number(e.target.value))}
                          style={smallInput}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rslSignalSettingsErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '10px' }}>⚠ {rslSignalSettingsErr}</div>}
        {rslSignalSettingsMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '10px' }}>✓ {rslSignalSettingsMsg}</div>}
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>🧪 RSL Backtest Watchlist bearbeiten</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={backtestWatchlistKey}
              onChange={e => ladeBacktestWatchlist(e.target.value)}
              style={{
                background: '#ffffff08',
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                padding: '8px 10px',
              }}
            >
              <option value="hdax">HDAX</option>
              <option value="nasdaq100">NASDAQ 100</option>
              <option value="sp500">SP500</option>
              <option value="stoxx600">STOXX 600</option>
            </select>
            <Btn onClick={toggleBacktestNullOnly} color={backtestWatchlistNullOnly ? 'yellow' : 'ghost'} size="sm">
              Nur null: {backtestWatchlistNullOnly ? 'An' : 'Aus'}
            </Btn>
            <Btn onClick={() => ladeBacktestWatchlist(backtestWatchlistKey)} color="ghost" size="sm">Neu laden</Btn>
          </div>
        </div>
        <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '8px' }}>
          Datei: {backtestWatchlistFile || '—'}
          {backtestWatchlistNullOnly ? ' · Filter zeigt nur aktuell gueltige Zeilen mit Enddatum null' : ''}
        </div>
        <textarea
          value={backtestWatchlistVisibleContent}
          onChange={e => {
            setBacktestWatchlistVisibleContent(e.target.value);
            if (!backtestWatchlistNullOnly) {
              setBacktestWatchlistContent(e.target.value);
            }
          }}
          style={{
            width: '100%',
            minHeight: '300px',
            background: themeMode === 'light' ? '#f7fbfd' : '#0b0d12',
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '10px',
            padding: '12px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '12px',
            boxSizing: 'border-box',
            marginBottom: '10px',
          }}
        />
        {backtestWatchlistErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {backtestWatchlistErr}</div>}
        {backtestWatchlistMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '10px' }}>✓ {backtestWatchlistMsg}</div>}
        {backtestWatchlistNullOnly && (
          <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '10px' }}>
            Im Null-Filter kannst Du vorhandene aktive Zeilen bearbeiten. Zum Hinzufügen oder Entfernen von Zeilen den Filter ausschalten.
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Btn onClick={pruefeBacktestWatchlistSyntax} color="ghost" size="sm">Syntax prüfen</Btn>
          <Btn onClick={speichernBacktestWatchlist} color="green" size="sm">Backtest-Watchlist speichern</Btn>
        </div>
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px' }}>⚙️ RSL Backtest-Modus</h3>
            <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '6px' }}>
              Legt fest, welche Backtest-Version User in der App verwenden.
            </div>
          </div>
          <Btn onClick={ladeRslBacktestMode} color="ghost" size="sm">Neu laden</Btn>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <select
            value={rslBacktestMode}
            onChange={e => setRslBacktestMode(e.target.value)}
            style={{
              minWidth: '280px',
              background: '#ffffff08',
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              padding: '8px 10px',
            }}
          >
            <option value="next_daily_open">v0.2 Tages-Open</option>
            <option value="historical_strategy_comparison_v09">v0.9 1990 - 18.05.2026</option>
            <option value="historical_strategy_comparison_v10">v1.0 1990 - gestern</option>
            <option value="historical_admin_signal_v11">v1.1 Scanner-Strategie</option>
            <option value="parameter_comparison">Parameter-Vergleich</option>
          </select>
          <Btn onClick={speichernRslBacktestMode} color="green" size="sm">Modus speichern</Btn>
        </div>
        {(rslBacktestMode === 'historical_strategy_comparison_v09' || rslBacktestMode === 'historical_strategy_comparison_v10') && (
          <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.6, marginTop: '10px', maxWidth: '760px' }}>
            Historischer Vergleichsmodus: Fuehrt S1, K1 bis K4, D1 bis D4 und die Sektor-ETF-Varianten parallel aus.
            v0.9 nutzt den Norgate-Snapshot bis 18.05.2026. v1.0 schreibt die historische Datenbasis bis gestern fort.
          </div>
        )}
        {rslBacktestMode === 'historical_admin_signal_v11' && (
          <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.6, marginTop: '10px', maxWidth: '760px' }}>
            v1.1 nutzt die je Index gepflegten RSL-Scanner Signal-Parameter und zeigt Usern nur Markt, Titelanzahl, Invest je Titel und Gebühr.
          </div>
        )}
        {rslBacktestMode === 'parameter_comparison' && (
          <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.6, marginTop: '10px', maxWidth: '760px' }}>
            Parameter-Vergleich nutzt vorberechnete Ergebnisdateien. Aktuell ist Nasdaq 100 verfügbar.
          </div>
        )}
        {rslBacktestModeErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '10px' }}>⚠ {rslBacktestModeErr}</div>}
        {rslBacktestModeMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '10px' }}>✓ {rslBacktestModeMsg}</div>}
      </Card>

        </>
      )}

      {adminSection === 'live_agents' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div style={{ color: COLORS.muted, fontSize: '12px' }}>
              Auto-Refresh alle 15 Sekunden
              {liveAgentsUpdatedAt ? ` · zuletzt aktualisiert ${formatDateTime(liveAgentsUpdatedAt)}` : ''}
            </div>
            <Btn onClick={() => ladeLiveAgents()} color="ghost" size="sm">Aktualisieren</Btn>
          </div>

          <Card>
            <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.7, marginBottom: '14px' }}>
              Übersicht aller Trade-Journal-Konten mit konfiguriertem oder bereits aktivem Einfach-Trading.de Live Agent.
            </div>
            {liveAgentsErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '10px' }}>⚠ {liveAgentsErr}</div>}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#ffffff05' }}>
                  <tr>
                    <th style={th}>Agent-E-Mail</th>
                    <th style={th}>Agent-Name</th>
                    <th style={th}>Status</th>
                    <th style={th}>Letzter Kontakt</th>
                    <th style={th}>TWS</th>
                    <th style={th}>Quote-Laufzeit</th>
                    <th style={th}>Fehler</th>
                    <th style={th}>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {liveAgents.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ ...td, textAlign: 'center', color: COLORS.muted }}>
                        Keine Live-Agents vorhanden
                      </td>
                    </tr>
                  )}
                  {liveAgents.map(item => (
                    <tr
                      key={`${item.account_id}-${item.agent_email}`}
                      style={{ background: selectedLiveAgentKey === `${item.account_id}-${item.agent_email}` ? '#ffffff08' : 'transparent' }}
                    >
                      <td style={{ ...td, fontWeight: 700 }}>{item.agent_email || '—'}</td>
                      <td style={td}>{item.agent_name || '—'}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <Badge color={getLiveAgentBadgeColor(item.status_color)}>
                            {item.status_label || '—'}
                          </Badge>
                          {item.agent_version ? (
                            <span style={{ color: COLORS.muted, fontSize: '12px', fontWeight: 600 }}>
                              v{String(item.agent_version).replace(/^v/i, '')}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td style={td}>
                        <div>{formatDateTime(item.last_seen_at)}</div>
                        <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '3px' }}>{formatRelativeAge(item.last_seen_at)}</div>
                      </td>
                      <td style={td}>
                        <div>{formatDateTime(item.last_tws_connected_at)}</div>
                        <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '3px' }}>{formatRelativeAge(item.last_tws_connected_at)}</div>
                      </td>
                      <td style={td}>{formatDurationMs(item.last_quote_duration_ms)}</td>
                      <td style={{ ...td, color: item.last_error ? COLORS.red : COLORS.muted }}>
                        {item.last_error ? 'Vorhanden' : '—'}
                      </td>
                      <td style={td}>
                        <Btn
                          onClick={() => setSelectedLiveAgentKey(currentKey => (
                            currentKey === `${item.account_id}-${item.agent_email}` ? '' : `${item.account_id}-${item.agent_email}`
                          ))}
                          color="ghost"
                          size="sm"
                        >
                          {selectedLiveAgentKey === `${item.account_id}-${item.agent_email}` ? 'Schließen' : 'Details'}
                        </Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(() => {
              const selectedLiveAgent = liveAgents.find(item => `${item.account_id}-${item.agent_email}` === selectedLiveAgentKey);
              if (!selectedLiveAgent) return null;
              return (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: `1px solid ${COLORS.border}`,
                    background: '#ffffff05',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: COLORS.text }}>
                        {selectedLiveAgent.agent_name || selectedLiveAgent.agent_email || 'Live Agent'}
                      </div>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }}>{selectedLiveAgent.agent_email || '—'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Badge color={selectedLiveAgent.secret_set ? 'green' : 'gray'}>
                        Secret: {selectedLiveAgent.secret_set ? 'gesetzt' : 'fehlt'}
                      </Badge>
                      <Badge color="indigo">Setups: {Number(selectedLiveAgent.setup_count || 0)}</Badge>
                      <Badge color="green">Aktive Setups: {Number(selectedLiveAgent.enabled_setup_count || 0)}</Badge>
                      <Badge color="yellow">Trades: {Number(selectedLiveAgent.trade_count || 0)}</Badge>
                      <Badge color="red">Offen: {Number(selectedLiveAgent.open_trade_count || 0)}</Badge>
                      {selectedLiveAgent.last_error ? <Badge color="red">Fehler vorhanden</Badge> : null}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '4px' }}>Status-Details</div>
                      <div style={{ color: COLORS.text, fontSize: '13px', lineHeight: 1.6 }}>{selectedLiveAgent.status_detail || '—'}</div>
                    </div>
                    <div>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '4px' }}>Letzter Kontakt</div>
                      <div style={{ color: COLORS.text, fontSize: '13px' }}>{formatDateTime(selectedLiveAgent.last_seen_at)}</div>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '3px' }}>{formatRelativeAge(selectedLiveAgent.last_seen_at)}</div>
                    </div>
                    <div>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '4px' }}>Letzte Anfrage</div>
                      <div style={{ color: COLORS.text, fontSize: '13px' }}>{formatDateTime(selectedLiveAgent.last_request_at)}</div>
                    </div>
                    <div>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '4px' }}>Letzter Quote-Upload</div>
                      <div style={{ color: COLORS.text, fontSize: '13px' }}>{formatDateTime(selectedLiveAgent.last_quote_at)}</div>
                    </div>
                    <div>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '4px' }}>Letzte TWS-Verbindung</div>
                      <div style={{ color: COLORS.text, fontSize: '13px' }}>{formatDateTime(selectedLiveAgent.last_tws_connected_at)}</div>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '3px' }}>{formatRelativeAge(selectedLiveAgent.last_tws_connected_at)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 560px', minWidth: 0 }}>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '6px' }}>Letzter Fehler</div>
                      <div style={{ color: selectedLiveAgent.last_error ? COLORS.red : COLORS.muted, fontSize: '13px', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {selectedLiveAgent.last_error || 'Kein Fehler gespeichert'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                      {selectedLiveAgent.last_error ? (
                        <Btn onClick={() => copyLiveAgentText(selectedLiveAgent.last_error)} color="ghost" size="sm">
                          Fehler kopieren
                        </Btn>
                      ) : null}
                      {liveAgentCopyMsg ? <div style={{ color: COLORS.muted, fontSize: '12px' }}>{liveAgentCopyMsg}</div> : null}
                    </div>
                  </div>
                </div>
              );
            })()}
          </Card>
        </>
      )}

      {adminSection === 'live_agent_setup' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div />
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap' }}>
              <select
                value={selectedLiveAgentUnderlying}
                onChange={e => setSelectedLiveAgentUnderlying(e.target.value)}
                style={{ ...inputStyle, minWidth: '140px', marginBottom: 0 }}
              >
                {['SPX', 'SPY', 'QQQ', 'DIA', 'DAX'].map(symbol => (
                  <option key={symbol} value={symbol}>{symbol}</option>
                ))}
              </select>
              <Btn onClick={ladeLiveAgentConfig} color="ghost" size="sm" style={{ whiteSpace: 'nowrap', minWidth: '96px' }}>Neu laden</Btn>
            </div>
          </div>

          <Card>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginBottom: '18px' }}>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Preview-Timeout (s)</label>
                <input style={inputStyle} type="number" value={currentLiveAgentGlobalConfig.preview_timeout_seconds ?? 45} onChange={e => updateLiveAgentGlobalConfig('preview_timeout_seconds', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Heartbeat-Intervall (s)</label>
                <input style={inputStyle} type="number" value={currentLiveAgentGlobalConfig.heartbeat_interval_seconds ?? 10} onChange={e => updateLiveAgentGlobalConfig('heartbeat_interval_seconds', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Max Simultaneous Option Tickers</label>
                <input style={inputStyle} type="number" value={currentLiveAgentGlobalConfig.max_simultaneous_option_tickers ?? 48} onChange={e => updateLiveAgentGlobalConfig('max_simultaneous_option_tickers', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Delta-Warnschwelle</label>
                <input style={inputStyle} type="number" step="0.01" value={currentLiveAgentGlobalConfig.delta_warning_threshold ?? 0.08} onChange={e => updateLiveAgentGlobalConfig('delta_warning_threshold', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Default Exit-Wartezeit (s)</label>
                <input style={inputStyle} type="number" value={currentLiveAgentGlobalConfig.default_exit_wait_seconds ?? 5} onChange={e => updateLiveAgentGlobalConfig('default_exit_wait_seconds', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Strike-Schritt</label>
                <input style={inputStyle} type="number" value={currentLiveAgentUnderlyingConfig.strike_step ?? 0} onChange={e => updateLiveAgentUnderlyingConfig('strike_step', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Price-Preview Initial</label>
                <input style={inputStyle} type="number" value={currentLiveAgentUnderlyingConfig.price_preview_initial_strike_limit ?? 24} onChange={e => updateLiveAgentUnderlyingConfig('price_preview_initial_strike_limit', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Delta-Preview Initial Regular</label>
                <input style={inputStyle} type="number" value={currentLiveAgentUnderlyingConfig.delta_preview_initial_strike_limit_regular ?? 20} onChange={e => updateLiveAgentUnderlyingConfig('delta_preview_initial_strike_limit_regular', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Delta-Preview Initial Small Delta</label>
                <input style={inputStyle} type="number" value={currentLiveAgentUnderlyingConfig.delta_preview_initial_strike_limit_small_delta ?? 36} onChange={e => updateLiveAgentUnderlyingConfig('delta_preview_initial_strike_limit_small_delta', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Delta-Preview Fallback Regular</label>
                <input style={inputStyle} type="number" value={currentLiveAgentUnderlyingConfig.delta_preview_fallback_strike_limit_regular ?? 120} onChange={e => updateLiveAgentUnderlyingConfig('delta_preview_fallback_strike_limit_regular', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Delta-Preview Fallback Small Delta</label>
                <input style={inputStyle} type="number" value={currentLiveAgentUnderlyingConfig.delta_preview_fallback_strike_limit_small_delta ?? 160} onChange={e => updateLiveAgentUnderlyingConfig('delta_preview_fallback_strike_limit_small_delta', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Entry-Preis-Schritt</label>
                <input style={inputStyle} type="number" step="0.01" value={currentLiveAgentUnderlyingConfig.entry_price_adjustment_step ?? 0.05} onChange={e => updateLiveAgentUnderlyingConfig('entry_price_adjustment_step', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Entry-Versuche max</label>
                <input style={inputStyle} type="number" value={currentLiveAgentUnderlyingConfig.entry_max_attempts ?? 3} onChange={e => updateLiveAgentUnderlyingConfig('entry_max_attempts', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Fill-Wartezeit (s)</label>
                <input style={inputStyle} type="number" value={currentLiveAgentUnderlyingConfig.fill_wait_seconds ?? 5} onChange={e => updateLiveAgentUnderlyingConfig('fill_wait_seconds', e.target.value)} />
              </div>
            </div>
            {liveAgentConfigErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '12px', marginBottom: '10px' }}>⚠ {liveAgentConfigErr}</div>}
            {liveAgentConfigMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '12px', marginBottom: '10px' }}>✓ {liveAgentConfigMsg}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', marginBottom: '16px' }}>
              <Btn onClick={speichernLiveAgentConfig} color="green" size="sm">{selectedLiveAgentUnderlying} speichern</Btn>
            </div>
            <div
              style={{
                marginTop: '4px',
                padding: '14px 16px',
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                background: themeMode === 'light' ? '#f7fbfd' : '#ffffff05',
              }}
            >
              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Preview-Timeout (s):</strong> Maximale Laufzeit einer Preview-Suche, bevor der Agent sie kontrolliert abbricht. Das schützt davor, dass eine einzelne zähe IB-/TWS-Suche minutenlang hängt. Höhere Werte geben der Suche mehr Raum, niedrigere Werte machen den Agent reaktionsschneller.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Heartbeat-Intervall (s):</strong> Wie oft der Agent unabhängig vom Hauptloop einen Lebenszeichen-Heartbeat an die App sendet. Kleinere Werte halten den Verbindungsstatus frischer, erzeugen aber etwas mehr Serververkehr.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Max Simultaneous Option Tickers:</strong> Obergrenze, wie viele Options-Ticker der Agent gleichzeitig für eine Preview lädt. Höhere Werte verbreitern die Suche, können aber TWS und Marktdaten stärker belasten.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Delta-Warnschwelle:</strong> Ab welcher Abweichung zwischen Ziel-Delta und gefundenem Delta der Agent im Preview eine Warnung anzeigt. Ein kleiner Wert macht die Prüfung strenger, ein größerer Wert toleranter.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Default Exit-Wartezeit (s):</strong> Standardwartezeit für Exit-/Close-Versuche, wenn kein expliziter Wert am Trade gesetzt ist. Für DAX bleibt intern weiterhin mindestens ein höheres Sicherheitsminimum bestehen.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Strike-Schritt:</strong> Legt fest, in welchem ganzzahligen Raster der Agent Strikes vorfiltert. `0` bedeutet keine zusätzliche Rasterung. `1` erlaubt 480, 481, 482. `5` würde nur 480, 485, 490 zulassen. Das ist besonders nützlich, wenn ein Underlying viele theoretische oder bei IB schlecht qualifizierbare Zwischenstrikes liefert.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Price-Preview Initial:</strong> So viele Strikes prüft der Agent im ersten Price-Preview-Durchlauf bevorzugt rund um den Underlying-Kurs. Höhere Werte machen die Suche breiter und robuster, kosten aber etwas mehr Zeit und mehr Contract-Qualifizierungen.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Delta-Preview Initial Regular:</strong> Startmenge für normale Delta-Ziele. Das betrifft typische Ziele, bei denen nicht extrem weit OTM gesucht wird. Wenn der Agent den passenden Strike oft knapp verfehlt, hilft hier meist ein etwas höherer Wert.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Delta-Preview Initial Small Delta:</strong> Startmenge für kleine Delta-Ziele, also typischerweise sehr weit OTM. Diese Suche braucht oft mehr Kandidaten, weil kleine Deltas seltener direkt im ersten kleinen Kandidatensatz auftauchen.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Delta-Preview Fallback Regular:</strong> Erweiterte Suchmenge, wenn im Initial-Set kein brauchbarer regulärer Delta-Kandidat gefunden wurde. Das ist die zweite Sicherheitsstufe nach dem ersten Versuch.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Delta-Preview Fallback Small Delta:</strong> Erweiterte Suchmenge für kleine Delta-Ziele. Gerade bei langen Laufzeiten oder ungewöhnlichen Chains ist dieser Wert wichtig, damit der Agent bei sehr kleinen Deltas nicht zu früh aufgibt.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Entry-Preis-Schritt:</strong> Um wie viel der Agent den Preis pro Retry aggressiver macht. Bei `0,05` würde er zum Beispiel von `2,05` auf `2,00`, `1,95`, `1,90` weitergehen, je nach Orderrichtung und Strategie.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Entry-Versuche max:</strong> Maximale Anzahl von Entry-/Retry-Versuchen, bevor der Agent einen Entry als nicht gefüllt beendet. Höhere Werte erhöhen die Ausführungschance, können aber zu mehr Zeit im Pending-/Retry-Zustand führen.
                </div>
                <div style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: COLORS.text }}>Fill-Wartezeit (s):</strong> So lange wartet der Agent pro Versuch, bevor er den nächsten Retry startet oder nach dem letzten Versuch aufgibt. Für sehr liquide US-Produkte reichen oft kleine Werte, für langsamere oder speziellere Märkte kann ein höherer Wert sinnvoll sein.
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      {adminSection === 'system' && (
        <>
      <Card style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>🧾 Audit-Log</h3>
          <Btn onClick={ladeAuditLogs} color="ghost" size="sm">Aktualisieren</Btn>
        </div>
        <div style={{ maxHeight: '430px', overflowY: 'auto', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <th style={th}>Zeit</th>
                <th style={th}>Ausgelöst durch</th>
                <th style={th}>Ziel-User</th>
                <th style={th}>Aktion</th>
                <th style={th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ ...td, textAlign: 'center', color: COLORS.muted }}>
                    Keine Einträge
                  </td>
                </tr>
              )}
              {auditLogs.map(log => (
                <tr key={log.id}>
                  <td style={{ ...td, color: COLORS.muted }}>
                    {new Date(log.created_at).toLocaleString('de-DE')}
                  </td>
                  <td style={td}>{log.actor_email || log.actor_user_id}</td>
                  <td style={td}>{log.target_email || (log.target_user_id ? log.target_user_id : '—')}</td>
                  <td style={td}><Badge color="indigo">{log.action}</Badge></td>
                  <td style={{ ...td, whiteSpace: 'normal' }}>{log.details || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
        </>
      )}

      {adminSection === 'content' && (
        <ContentStudioSection
          COLORS={COLORS}
          th={th}
          td={td}
          onSessionExpired={onSessionExpired}
          onToast={onToast}
        />
      )}

      {adminSection === 'handbook' && (
        <>
          <Card style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '14px', color: COLORS.muted, lineHeight: 1.7 }}>
              Dieses Handbuch fasst den Zweck der einzelnen Admin-Bereiche zusammen und dient als interne Orientierung für Support, Betrieb und Pflege der App.
            </div>
          </Card>

          <div style={{ display: 'grid', gap: '16px' }}>
            {ADMIN_HANDBOOK_SECTIONS.map(section => (
              <Card key={section.title}>
                <button
                  type="button"
                  onClick={() => toggleHandbookSection(section.title)}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '800', marginBottom: '6px', color: COLORS.text }}>{section.title}</div>
                    <div style={{ fontSize: '14px', color: COLORS.muted, lineHeight: 1.6 }}>
                      {section.purpose}
                    </div>
                  </div>
                  <div style={{ fontSize: '18px', color: COLORS.cyan, flexShrink: 0 }}>
                    {openHandbookSections[section.title] ? '▴' : '▾'}
                  </div>
                </button>

                {openHandbookSections[section.title] && (
                  <div style={{ display: 'grid', gap: '14px', marginTop: '16px' }}>
                    {section.details.map(detail => (
                      <div
                        key={`${section.title}-${detail.label}`}
                        style={{
                          padding: '14px',
                          borderRadius: '12px',
                          border: `1px solid ${COLORS.border}`,
                          background: '#ffffff05',
                        }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: '800', color: COLORS.text, marginBottom: '8px' }}>
                          {detail.label}
                        </div>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {detail.items.map(item => (
                            <div key={item} style={{ fontSize: '13px', color: COLORS.muted, lineHeight: 1.6 }}>
                              • {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
